import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { selfServiceBaseUrl } from '../self-service-base-url';
import { WidgetRendererComponent } from '../widget-renderer/widget-renderer.component';
import { GlobalFilterComponent } from '@shared-lib/global-components/global-filter/global-filter.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { DashboardTitleService } from '@shared-lib/shared-services/dashboard-title.service';
import { PlatformService } from '@shared-lib/shared-services/platform.service.service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import * as echarts from 'echarts';
import { ThemeService } from '@shared-lib/shared-services/theme.service';
import { LayoutService } from '@shared-lib/shared-services/layout.service';
import { RoleVisibilityService } from '../shared/role-visibility/role-visibility.service';
import { KeycloakService } from 'keycloak-angular';
import { OnTheFlyComputationComponent, OtfAppliedConfig } from '@shared-lib/self-service-visualization/dashboard-builder/on-the-fly-computation/on-the-fly-computation.component';
import { SelfServiceDashboardService } from '@shared-lib/self-service-visualization/shared/self-service-dashboard.service';
import { readJson, writeDynamicDashboardCache, readDynamicDashboardCache } from '@shared-lib/self-service-visualization/shared/self-service-storage.utils';
import { getSelfServiceBaseUrl, setSelfServiceApiOverrides, buildSelfServiceApiUrl, getSelfServicePlatformGetDataApiUrl } from '@shared-lib/self-service-visualization/shared/self-service-api.utils';
import { buildRecomputedWidgetFields, recomputeEntryHasData, resolveRecomputeChartType } from '@shared-lib/self-service-visualization/shared/self-service-recompute.utils';
import { ShareDashboardDialogComponent } from '@shared-lib/self-service-visualization/dashboard-builder/share-dashboard-dialog/share-dashboard-dialog.component';
import { MatDialog } from '@angular/material/dialog';

interface GridItem {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW: number;
  maxW: number;
  minH: number;
  maxH: number;
  locked: boolean;
}

interface WidgetRec {
  id: string;
  widget: any;
}

/**
 * A single tab within a tabbed dashboard. Each tab owns its own set of
 * widgets (`pageContent`) and may optionally declare `filters` that are
 * scoped to just that tab (in addition to the dashboard-level global
 * filters, which apply across all tabs).
 */
interface TabDef {
  id: string;
  /** URL-friendly slug derived from the label (e.g. "Percentage View" →
   *  "percentage-view"). Used in the `?tab=` query param instead of the raw
   *  internal id, so shared links read cleanly. */
  slug: string;
  label: string;
  filters?: any;
  pageContent?: any[];
}

interface Interaction {
  mode: 'drag' | 'resize';
  id: string;
  corner?: 'nw' | 'ne' | 'sw' | 'se';
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
}

interface FocusModeSettings {
  enabled: boolean;
  includeTypes: Set<string>;
  excludeTypes: Set<string>;
}

interface TooltipSnapshotEntry {
  appendToBody?: boolean;
  appendTo?: unknown;
}

interface CompactViewportSnapshot {
  width: number;
  height: number;
  scale: number;
  requiredHeight: number;
}

interface PersistedLayoutSnapshot {
  version: number;
  layouts: Record<string, GridItem[]>;
}

interface LayoutPersistenceRequestConfig {
  apiUrl?: string;
  method?: string;
  dashboard_id?: string;
  dashboardId?: string;
  requestData?: Record<string, unknown>;
  defaultRequestData?: Record<string, unknown>;
  queryParams?: Record<string, unknown>;
  headers?: Record<string, string>;
  useQueryParams?: boolean;
  includeRequestContext?: boolean;
  responsePath?: string;
  layoutKey?: string;
  skipDefaultPayload?: boolean;
  customPayload?: Record<string, unknown>;
  entity?: string;
  workflowId?: string;
  functionName?: string;
  version?: string;
  communicationType?: string;
  enabled?: boolean;
}

interface LayoutPersistenceConfig extends LayoutPersistenceRequestConfig {
  load?: Partial<LayoutPersistenceRequestConfig>;
  save?: Partial<LayoutPersistenceRequestConfig>;
  emailOwnerMode?: boolean;
  allowDefaultSave?: boolean;
  adminEmails?: string[];
  adminRoles?: string[];
  fallbackAdminEmails?: string[];
  fallbackAdminEmail?: string;
  personalSaveLabel?: string;
  defaultSaveLabel?: string;
}

interface LayoutUserContext {
  userId: string | null;
  username: string | null;
  email: string | null;
  roleIds: string[];
}

interface PersistedLayoutRecord {
  emailId: string | null;
  uuid: string | null;
  role: string | null;
  sourceEmail: string;
  isFallback: boolean;
  raw: any;
}

interface PersistedLayoutLookupResult {
  layouts: Record<string, GridItem[]> | null;
  record: PersistedLayoutRecord | null;
}

type LayoutPersistenceScope = 'USER' | 'GLOBAL';
type LayoutPersistenceRequestMode = LayoutPersistenceScope | 'RESOLVE';

const BPS: Record<string, number> = { xl: 1536, lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const COLS: Record<string, number> = { xl: 12, lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const NON_FOCUS_WIDGET_TYPES = new Set<string>([
  'slider',
  'input-box',
  'configurable-form',
  'editable-table',
  'table',
  'dynamic-edit-table',
  'upload-file',
  'notes',
  'static-screen',
  'multiselect-toggle',
  'generic-table',
  'table-generic',
  'number-stats',
  'numberstatscomponent',
  'range-bidirectional-slider',
  'entity-table',
  'entity-form',
  'button',
  'landing-module-card',
  'json-driven-smart-table',
  'scenario-planner-table',
]);

@Component({
  selector: 'app-dashboard',
  imports: [
    WidgetRendererComponent,
    CommonModule,
    GlobalFilterComponent,
    MatTooltipModule,
    OnTheFlyComputationComponent,
  ],
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  providers: [GlobalFilterService, ResponsiveLayoutService],
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly COMPACT_BASELINE_BROWSER_ZOOM_MAX = 1.02;
  private static readonly COMPACT_EXIT_MARGIN_PX = 24;
  private static readonly COMPACT_ENTER_MARGIN_PX = 72;

  @ViewChild('dashboardContent') dashboardContentRef?: ElementRef<HTMLElement>;
  @ViewChild('gridContainer') gridContainerRef?: ElementRef<HTMLElement>;
  @Input() configFile?: string;
  @Input() dashboardSourceKey?: string;
  @HostBinding('class.no-scroll') get noScrollClass() {
    return !this.responsiveLayoutService.effectiveScreenFloating;
  }
  @HostBinding('class.focus-active') get focusActiveClass() {
    return !!this.focusedWidgetId;
  }
  /** Host flag: enables the richer tabbed-dashboard styling (pill tabs, polished
   *  filter bar + card headers) without leaking those styles to classic
   *  single-page dashboards. */
  @HostBinding('class.has-tabs-ui') get hasTabsUiClass() {
    return this.hasTabs;
  }

  configurations: any = {};
  /** Tabs declared by the dashboard config (empty for a classic single-page
   *  dashboard). When populated, only the active tab's pageContent is fed to
   *  the grid pipeline at a time. */
  tabs: TabDef[] = [];
  /** Id of the currently-shown tab, or null for a single-page dashboard. */
  activeTabId: string | null = null;
  /** Local (per-tab) filter values remembered for the session, keyed by tab id.
   *  Global filter values live in GlobalFilterService and persist across tabs. */
  private tabLocalFilterState = new Map<string, any>();
  /** Identity of the dashboard currently loaded (slug / configFile / appId).
   *  Used to distinguish a tab-only query-param change from navigation to a
   *  different dashboard. */
  private loadedRouteIdentity: string | null = null;
  /** Memoized resolved account-profile email, so we don't re-await
   *  loadUserProfile() on every dashboard navigation (it's stable per session). */
  private resolvedEmailMemo: string | null = null;
  screenFloating: boolean = true;
  configFileName!: string;
  sourceKey = 'dashboard';
  widgets: WidgetRec[] = [];
  widgetMap = new Map<string, WidgetRec>();
  layouts: Record<string, GridItem[]> = {};
  currentBp = 'lg';
  containerWidth = 0;
  activeDragId: string | null = null;
  activeResizeId: string | null = null;
  focusedWidgetId: string | null = null;
  focusMode: FocusModeSettings = {
    enabled: false,
    includeTypes: new Set<string>(),
    excludeTypes: new Set<string>(),
  };
  editMode = false;
  layoutEditEnabled = false;
  grid = {
    version: 1,
    breakpoints: { ...BPS },
    columns: { ...COLS },
    rowHeight: 72,
    gap: 12,
    draggable: true,
    resizable: true,
    persist: true,
    persistKey: '',
    defaultW: 4,
    defaultH: 4,
    defaultMinW: 2,
    defaultMinH: 2,
    defaultMaxH: 16,
    rawLayouts: {} as Record<string, Array<Record<string, unknown>>>,
  };

  private subs = new Subscription();
  private layoutService = inject(LayoutService);
  private selfServiceDashboardService = inject(SelfServiceDashboardService);
  private dialog = inject(MatDialog);
  /** Untouched response from the most recent loadCharts/loadDashboardFromApi.
   *  Held so the LayoutService can reshape its pageContent on layout switches
   *  without re-fetching the dashboard. */
  private originalDashboardConfig: any = null;
  /** JSON snapshot of the last applied pageContent — lets the layout
   *  subscription skip re-renders when the resolved layout is unchanged. */
  private appliedLayoutSignature: string | null = null;
  private interaction?: Interaction;
  private interactionTarget?: HTMLElement;
  private queued: { x: number; y: number } | null = null;
  private raf = false;
  private gridObserver?: ResizeObserver;
  private tooltipAppendSnapshot = new WeakMap<HTMLElement, TooltipSnapshotEntry[]>();
  private focusTooltipSyncTimer?: ReturnType<typeof setInterval>;
  private compactFitRafId: number | null = null;
  private compactScrollLock = false;
  private compactScrollLockSnapshot: CompactViewportSnapshot | null = null;
  private navFiltersApplied = false;
  private persistedLayouts: Record<string, GridItem[]> | null = null;
  private persistedLayoutRecord: PersistedLayoutRecord | null = null;
  private editSessionLayouts: Record<string, GridItem[]> | null = null;

  get effectiveScreenFloating(): boolean {
    return this.responsiveLayoutService.effectiveScreenFloating;
  }

  get isInteracting(): boolean {
    return !!this.interaction;
  }

  get hasFocusWidget(): boolean {
    return !!this.focusedWidgetId;
  }

  get orderedBps(): string[] {
    return Object.entries(this.grid.breakpoints).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }

  get layoutSavePersonalLabel(): string {
    return this.resolveLayoutPersistenceConfig(this.configurations)?.personalSaveLabel
      || 'Save My Layout';
  }

  get layoutSaveDefaultLabel(): string {
    return this.resolveLayoutPersistenceConfig(this.configurations)?.defaultSaveLabel
      || 'Save As Default';
  }

  get canSaveDefaultLayout(): boolean {
    if (!this.editMode || !this.layoutEditEnabled) return false;

    const persistence = this.resolveLayoutPersistenceConfig(this.configurations);
    if (!persistence || persistence.allowDefaultSave === false) return false;

    const userRoles = new Set(
      this.getCurrentLayoutUserContext().roleIds.map((role) => role.trim().toLowerCase())
    );
    if (!userRoles.size) return false;

    const configuredAdminRoles = (persistence.adminRoles || [])
      .map((role) => String(role || '').trim().toLowerCase())
      .filter((role) => !!role);

    if (configuredAdminRoles.length) {
      return configuredAdminRoles.some((role) => userRoles.has(role));
    }

    return [...userRoles].some((role) => role.includes('admin'));
  }

  get cols(): number {
    return this.grid.columns[this.currentBp] ?? 12;
  }

  get gridStyle(): Record<string, string> {
    return {
      'grid-template-columns': `repeat(${this.cols}, minmax(0, 1fr))`,
      'grid-auto-rows': `${this.grid.rowHeight}px`,
      // Custom Theme tab's "Widget spacing" choice writes --dvm-widget-gap;
      // when unset, fall back to the dashboard JSON's own gap value.
      gap: `var(--dvm-widget-gap, ${this.grid.gap}px)`,
    };
  }

  get dashboardNameFontSize(): string {
    return this.normalizeDashboardNameFontSize(
      this.configurations?.dashboardNameFontSize ??
      this.configurations?.['dashboard-name-font-size'] ??
      this.configurations?.['dashboardNamefontsize'] ??
      this.configurations?.['dashboardNAmefontsize']
    );
  }

  get hasTabs(): boolean {
    return this.tabs.length > 0;
  }

  get activeTab(): TabDef | null {
    if (!this.tabs.length) return null;
    return this.tabs.find((t) => t.id === this.activeTabId) ?? this.tabs[0];
  }

  /** The active tab's URL slug (what goes in `?tab=`). */
  get activeTabSlug(): string | null {
    return this.activeTab?.slug ?? null;
  }

  /** The active tab's local filter config, or null when the tab has none. */
  get activeTabFilters(): any {
    return this.activeTab?.filters ?? null;
  }

  /** Resolve a `?tab=` value to a tab, matching by slug first (clean URLs),
   *  then by raw internal id (backward compatible with old shared links). */
  private findTabByUrlKey(tabs: TabDef[], key: string | null): TabDef | null {
    if (!key) return null;
    return (
      tabs.find((t) => t.slug === key) ||
      tabs.find((t) => t.id === key) ||
      null
    );
  }

  /** Placement of the dashboard-level global filter bar relative to the grid.
   *  Read from `config.filters.position` (self-service dashboards persist
   *  top/bottom/left/right here). Anything unrecognised falls back to 'top' so
   *  older configs render unchanged. Left/right render the bar as a vertical
   *  side panel; top/bottom render it as a horizontal strip. */
  get globalFiltersPosition(): 'top' | 'bottom' | 'left' | 'right' {
    const raw = String(
      this.configurations?.filters?.position ??
      this.configurations?.filters?.placement ??
      'top',
    ).trim().toLowerCase();
    return raw === 'left' || raw === 'right' || raw === 'bottom' ? (raw as 'left' | 'right' | 'bottom') : 'top';
  }

  get viewItems(): Array<{ item: GridItem; rec: WidgetRec }> {
    const l = this.layouts[this.currentBp] ?? [];
    return l
      .map((item) => {
        const rec = this.widgetMap.get(item.id);
        return rec ? { item, rec } : null;
      })
      .filter((x): x is { item: GridItem; rec: WidgetRec } => !!x);
  }

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    public configService: ConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private globalFilterService: GlobalFilterService,
    private platformService: PlatformService,
    private responsiveLayoutService: ResponsiveLayoutService,
    private themeService: ThemeService,
    private dashboardTitleService: DashboardTitleService,
    private roleVisibilityService: RoleVisibilityService,
    private keycloakService: KeycloakService,
    private http: HttpClient,
  ) { }

  isSelfServicePublished = false;
  /** True only for the shared read-only view (`/dynamic-dashboard/:slug`, opened
   *  from the Share link). The "Back to Builder" action is meaningful only for the
   *  creator's own publish view (`/:slug`), so it is hidden when this is true. */
  isSharedReadonlyView = false;

  otfOpen = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  otfColumns: any[] | null = null;
  otfEntity: string | null = null;
  otfRows: Array<Record<string, unknown>> = [];
  otfWidget: any = null;

  canShowOtfButton(widget: any): boolean {
    return this.isSelfServicePublished && ['input-box', 'slider'].includes(String(widget?.ssWidgetKind ?? ''));
  }

  openWidgetOtf(widget: any, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canShowOtfButton(widget)) return;
    this.otfWidget = widget;
    this.otfColumns = widget?.computationColumns ?? widget?.selfService?.columns ?? widget?.dataset?.columns ?? null;
    this.otfEntity = widget?.computationEntityName ?? widget?.dataset?.fileName ?? null;
    this.otfRows = widget?.computationRows ?? widget?.selfService?.rawRows ?? widget?.dataset?.rows ?? [];
    this.otfOpen = true;
  }

  closeWidgetOtf(): void {
    this.otfOpen = false;
    this.otfColumns = null;
    this.otfEntity = null;
    this.otfRows = [];
    this.otfWidget = null;
  }


  onPublishedOtfRows(rows: Array<Record<string, unknown>>): void {
    this.applyPublishedOtfRows(rows);
  }

  onPublishedOtfDataResult(result: Record<string, unknown> | Array<Record<string, unknown>>): void {
    this.applyPublishedOtfRows(this.normalizePublishedOtfRows(result));
  }

  onPublishedOtfComputationApplied(config: OtfAppliedConfig): void {
    if (!this.otfWidget) return;
    this.otfWidget.onTheFlyComputation = config;
    const widgetId = String(this.otfWidget.widgetId ?? this.otfWidget.id ?? '').replace(/[^a-zA-Z0-9-_]/g, '-');
    const rec = widgetId ? this.widgetMap.get(widgetId) : null;
    if (rec?.widget) {
      rec.widget.onTheFlyComputation = config;
      this.otfWidget = rec.widget;
    }
  }

  onPublishedWidgetValueChange(widget: any, value: Record<string, unknown>): void {
    this.persistPublishedWidgetValue(widget, value);
    const config = widget?.onTheFlyComputation as OtfAppliedConfig | undefined;
    if (!config?.expressions?.length || !config.entityName) return;

    const context = {
      sliderValue: value['sliderValue'] ?? value['widgetValue'] ?? config.sliderValue,
      inputValue: value['inputValue'] ?? value['widgetValue'] ?? config.sliderValue,
      widgetValue: value['widgetValue'] ?? value['sliderValue'] ?? value['inputValue'] ?? config.sliderValue,
      rangeStart: value['rangeStart'],
      rangeEnd: value['rangeEnd'],
      ...value,
    };

    const resolveTokens = (expr: string) => Object.entries(context).reduce(
      (next, [key, raw]) => next.replace(new RegExp(`\\b${key}\\b`, 'g'), String(raw ?? '')),
      expr,
    );

    this.http.post<Record<string, unknown> | Array<Record<string, unknown>>>(
      getSelfServicePlatformGetDataApiUrl(),
      {
        entityName: config.entityName,
        attributesList: config.expressions.map((expr) => resolveTokens(expr.raw)),
        filterList: this.buildPublishedOtfFilterList(),
      },
    ).subscribe({
      next: (result) => this.applyPublishedOtfRows(this.normalizePublishedOtfRows(result)),
      error: (error) => console.error('Failed to run published on-the-fly computation', error),
    });
  }

  /** A DVM-layout input control (slider / input box) changed in the published
   *  view: re-post its payload with the new value to the compute API and apply
   *  the per-chart `results[]` to the target chart widgets in place. */
  /** True while ANY published input-control recompute POST is in flight — kept
   *  only for callers that need a coarse "something is recomputing" flag (e.g.
   *  disabling the OTF button). The per-chart loading indicator below
   *  (`recomputingComponentIds`) is what actually drives the UI now, so an
   *  in-flight recompute no longer dims/overlays the WHOLE dashboard — only the
   *  charts this input actually targets show a loading state. */
  isRecomputing = false;
  /** componentIds of the charts a recompute currently in flight will update —
   *  drives `isWidgetRecomputing()` so only THOSE widgets show a loading
   *  overlay instead of a full-dashboard one. */
  private recomputingComponentIds = new Set<string>();

  /** True when this widget is one of the targets of a recompute currently in
   *  flight — used by the template to show a per-widget loading overlay. */
  isWidgetRecomputing(widget: any): boolean {
    const id = String(widget?.componentId ?? '');
    return !!id && this.recomputingComponentIds.has(id);
  }

  onDvmInputRecompute(widget: any, rawValue: unknown): void {
    // Slider emits { start, end }; input box emits a scalar.
    const value =
      rawValue && typeof rawValue === 'object' && 'end' in (rawValue as Record<string, unknown>)
        ? (rawValue as Record<string, unknown>)['end']
        : rawValue;
    const payload = { ...(widget?.payload ?? {}), value };
    widget.payload = payload; // persist so successive changes send the latest
    const url =
      String(widget?.recomputeUrl || '').trim() ||
      // Correct compute endpoint (base + /components/compute) so a dashboard
      // published without a persisted recomputeUrl still recomputes.
      buildSelfServiceApiUrl('/components/compute');
    // Only this input's OWN targets may be updated from its recompute response —
    // the compute API can echo back entries for OTHER charts too (e.g. leftover
    // state from a different input control), and those must be left alone even
    // when they carry real-looking data.
    const targetIds = (Array.isArray(payload['targets']) ? (payload['targets'] as Array<Record<string, unknown>>) : [])
      .map((t) => String(t?.['componentId'] ?? ''))
      .filter(Boolean);
    this.isRecomputing = true;
    this.recomputingComponentIds = new Set(targetIds);
    // eslint-disable-next-line no-console
    console.log('[dvm-recompute] POST', url, 'targetIds=', targetIds, 'payload=', payload);
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        // eslint-disable-next-line no-console
        console.log('[dvm-recompute] response=', response);
        this.applyDvmRecomputeResults(response, targetIds);
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
      },
      error: (error) => {
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
        console.error('[dvm-layout] input recompute failed', error);
      },
    });
  }

  /** Apply an input-control recompute response `{ inputComponentId, results:[…] }`
   *  to the published widgets: match each result to a widget by `componentId`
   *  (charts carry a fixed, type-derived id) and rebuild its chart data via the
   *  shared builders, then swap `widgets`/`widgetMap` refs so WidgetRenderer
   *  re-renders each target chart in place. */
  private applyDvmRecomputeResults(response: Record<string, unknown> | null, targetIds: string[] = []): void {
    const results = (response?.['results'] ?? []) as Array<Record<string, unknown>>;
    if (!Array.isArray(results) || !results.length) return;
    // Match by CHART TYPE only as a fallback. The response's `componentId` is
    // the exact target id the recompute payload sent, so it identifies ONE
    // specific widget even when several widgets share the same chart type
    // (e.g. multiple KPI cards) — keying by type alone would collapse them.
    const chartTypeToWidgetType: Record<string, string> = {
      WATERFALL: 'waterfall-chart',
      DRILL_DOWN_TABLE: 'drill-down',
      HORIZONTAL_BAR_CHART: 'horizontal-bar-chart',
      KPI: 'kpi-card',
    };
    const allowedIds = new Set(targetIds);
    const byComponentId = new Map<string, Record<string, unknown>>();
    const byWidgetType = new Map<string, Record<string, unknown>>();
    results.forEach((entry) => {
      // Skip placeholder entries with no data — the compute API returns an entry
      // for every target chart but only recomputes some; applying an empty one
      // would blank/stick that chart in a loading state.
      const hasData = recomputeEntryHasData(entry);
      const entryComponentId = String(entry?.['componentId'] ?? '');
      const resolvedType = resolveRecomputeChartType(entry);
      // eslint-disable-next-line no-console
      console.log('[dvm-recompute] entry', entryComponentId, 'resolvedType=', resolvedType, 'hasData=', hasData, entry);
      if (!hasData) return;
      // Only this input's OWN targets (from its payload.targets) may be applied —
      // the compute API can echo back entries for charts this input doesn't
      // drive at all, and those must be left untouched even with real data.
      if (allowedIds.size && (!entryComponentId || !allowedIds.has(entryComponentId))) {
        // eslint-disable-next-line no-console
        console.log('[dvm-recompute] SKIPPED (not in targetIds)', entryComponentId, 'allowedIds=', [...allowedIds]);
        return;
      }
      if (entryComponentId) byComponentId.set(entryComponentId, entry);
      // Resolve the chart type (drill-down comes back with chartType null +
      // componentType 'TABLE'), then map it to the published widget `type`.
      const widgetType = chartTypeToWidgetType[resolvedType];
      if (widgetType) byWidgetType.set(widgetType, entry);
    });
    let changed = false;
    const nextWidgets = this.widgets.map((rec) => {
      const widget = rec.widget as any;
      const entry =
        byComponentId.get(String(widget?.componentId ?? '')) ?? byWidgetType.get(String(widget?.type ?? ''));
      const isMarginPostSsa = String(widget?.componentId ?? '') === 'CMP-SPEC-KPI-MARGINPOSTSSA';
      if (widget?.type === 'waterfall-chart' || isMarginPostSsa) {
        // eslint-disable-next-line no-console
        console.log('[dvm-recompute] widget', widget?.componentId, widget?.type, 'byComponentId has=', byComponentId.has(String(widget?.componentId ?? '')), 'byWidgetType has=', byWidgetType.has(String(widget?.type ?? '')), 'matched entry=', entry);
      }
      if (!entry) return rec;
      const fields = buildRecomputedWidgetFields(entry, {
        widgetId: String(widget?.widgetId ?? rec.id),
        title: widget?.title,
        yAxisName: widget?.yAxisName,
        kpiField: widget?.payload?.field,
        format: widget?.format,
      });
      if (widget?.type === 'waterfall-chart' || isMarginPostSsa) {
        // eslint-disable-next-line no-console
        console.log('[dvm-recompute] widget', widget?.componentId, 'built fields=', fields);
      }
      if (!fields) return rec;
      changed = true;
      // Merge the rebuilt data fields but PRESERVE identity / layout / size so
      // the tile doesn't move or resize on data refresh.
      const mergedWidget = {
        ...widget,
        ...fields,
        type: widget.type,
        componentId: widget.componentId,
        widgetId: widget.widgetId,
        title: widget.title,
        layout: widget.layout,
        height: widget.height,
        payload: widget.payload,
      };
      if (widget?.type === 'waterfall-chart' || isMarginPostSsa) {
        // eslint-disable-next-line no-console
        console.log('[dvm-recompute] widget', widget?.componentId, 'mergedWidget=', mergedWidget);
      }
      return { ...rec, widget: mergedWidget };
    });
    if (changed) {
      this.widgets = nextWidgets;
      this.widgetMap = new Map(this.widgets.map((w) => [w.id, w]));
    }
  }


  private persistPublishedWidgetValue(widget: any, value: Record<string, unknown>): void {
    if (!widget || typeof widget !== 'object') return;
    const patch = this.buildPublishedWidgetValuePatch(widget, value);
    if (!Object.keys(patch.widgetConfig).length) return;

    Object.assign(widget, patch.topLevel);
    widget.widgetConfig = { ...(widget.widgetConfig ?? {}), ...patch.widgetConfig };

    const widgetId = String(widget.widgetId ?? widget.id ?? '').replace(/[^a-zA-Z0-9-_]/g, '-');
    if (!widgetId) return;

    for (const rec of this.widgets) {
      if (rec.id !== widgetId && rec.widget !== widget) continue;
      Object.assign(rec.widget, patch.topLevel);
      rec.widget.widgetConfig = { ...(rec.widget?.widgetConfig ?? {}), ...patch.widgetConfig };
      if (rec.widget === widget || rec.id === widgetId) {
        this.otfWidget = rec.widget;
      }
      this.widgetMap.set(rec.id, rec);
    }
  }

  private buildPublishedWidgetValuePatch(
    _widget: any,
    value: Record<string, unknown>,
  ): { widgetConfig: Record<string, unknown>; topLevel: Record<string, unknown> } {
    const widgetConfig: Record<string, unknown> = {};
    const topLevel: Record<string, unknown> = {};

    if (value['sliderValue'] !== undefined || value['widgetValue'] !== undefined) {
      const next = value['sliderValue'] ?? value['widgetValue'];
      widgetConfig['defaultValue'] = next;
      topLevel['defaultValue'] = next;
    }
    if (value['inputValue'] !== undefined) {
      widgetConfig['defaultValue'] = value['inputValue'];
      topLevel['defaultValue'] = value['inputValue'];
    }
    if (value['rangeStart'] !== undefined) {
      widgetConfig['rangeStart'] = value['rangeStart'];
      topLevel['rangeStart'] = value['rangeStart'];
    }
    if (value['rangeEnd'] !== undefined) {
      widgetConfig['rangeEnd'] = value['rangeEnd'];
      topLevel['rangeEnd'] = value['rangeEnd'];
    }

    return { widgetConfig, topLevel };
  }
  private normalizePublishedOtfRows(result: Record<string, unknown> | Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    if (Array.isArray(result)) return result;
    const values = Object.values(result ?? {});
    const nested = values.find((value): value is Array<Record<string, unknown>> => Array.isArray(value));
    return nested ?? (result ? [result] : []);
  }

  private applyPublishedOtfRows(rows: Array<Record<string, unknown>>): void {
    if (!rows.length) return;
    const computedKeys = new Set(rows.flatMap((row) => Object.keys(row)));
    const nextWidgets = this.widgets.map((rec) => {
      const widget = rec.widget;
      if (widget?.ssWidgetKind || !this.publishedWidgetUsesAnyColumn(widget, computedKeys)) {
        return rec;
      }

      const baseRows = (widget?.__otfBaseRows ?? widget?.selfService?.rawRows ?? widget?.computationRows ?? widget?.dataset?.rows ?? []) as Array<Record<string, unknown>>;
      const filteredBaseRows = this.filterPublishedRowsForCurrentFilters(baseRows);
      const rowsToMerge = filteredBaseRows.length === rows.length
        ? filteredBaseRows
        : (baseRows.length === rows.length ? baseRows : []);
      if (!rowsToMerge.length && baseRows.length) return rec;

      const mergedRows = (rowsToMerge.length ? rowsToMerge : rows).map((row, index) => ({
        ...row,
        ...(rows[index] ?? {}),
      }));
      const mergedWidget = { ...widget, __otfBaseRows: [...baseRows] };

      if (mergedWidget.selfService) {
        mergedWidget.selfService = {
          ...mergedWidget.selfService,
          rawRows: mergedRows,
          columns: this.mergePublishedColumns(mergedWidget.selfService.columns ?? [], computedKeys),
        };
      }
      mergedWidget.computationRows = mergedRows;
      mergedWidget.computationColumns = this.mergePublishedColumns(
        mergedWidget.computationColumns ?? mergedWidget.selfService?.columns ?? [],
        computedKeys,
      );
      if (mergedWidget.dataset) {
        mergedWidget.dataset = {
          ...mergedWidget.dataset,
          rows: mergedRows,
          columns: this.mergePublishedColumns(mergedWidget.dataset.columns ?? [], computedKeys),
        };
      }

      return { ...rec, widget: mergedWidget };
    });

    this.widgets = nextWidgets;
    this.widgetMap = new Map(this.widgets.map((w) => [w.id, w]));
  }


  private filterPublishedRowsForCurrentFilters(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    if (!rows.length) return rows;
    const filters = this.globalFilterService.getCurrentFilters();
    const params = { ...(filters?.requestParams ?? {}), ...(filters?.queryParams ?? {}) };
    const active = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== '');
    if (!active.length) return rows;

    return rows.filter((row) => active.every(([key, value]) => {
      if (!(key in row)) return true;
      const allowed = (Array.isArray(value) ? value : [value]).map((item) => String(item));
      return allowed.includes(String(row[key] ?? ''));
    }));
  }
  private publishedWidgetUsesAnyColumn(widget: any, keys: Set<string>): boolean {
    const selfService = widget?.selfService ?? {};
    const mapped = [
      selfService.categoryKey,
      selfService.valueKey,
      selfService.xAxis,
      selfService.yAxis,
      selfService.kpiKey,
      ...(Object.values(widget?.ssChartMeta?.mapping ?? {}) as string[]),
    ].filter((value): value is string => typeof value === 'string' && !!value);
    const columns = [
      ...(selfService.columns ?? []).map((col: any) => col?.name),
      ...(widget?.computationColumns ?? []).map((col: any) => col?.name),
    ].filter((value): value is string => typeof value === 'string' && !!value);
    return [...mapped, ...columns].some((column) => keys.has(column));
  }

  private mergePublishedColumns(columns: any[], keys: Set<string>): any[] {
    const existing = new Set((columns ?? []).map((col) => col?.name).filter(Boolean));
    return [
      ...(columns ?? []),
      ...[...keys]
        .filter((key) => !existing.has(key))
        .map((name) => ({ name, type: 'number' })),
    ];
  }

  private buildPublishedOtfFilterList(): Array<Record<string, string>> {
    const filters = this.globalFilterService.getCurrentFilters();
    const params = { ...(filters?.requestParams ?? {}), ...(filters?.queryParams ?? {}) };
    const entries = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .flatMap(([key, value]) => (Array.isArray(value) ? value : [value]).map((item) => ({ key, value: item })));
    return entries.map(({ key, value }, index) => ({
      operator: '=',
      attributeName: key,
      attributeValue: String(value),
      logicalOperator: index < entries.length - 1 ? 'AND' : '',
    }));
  }

  /** Open the read-only share-link dialog for the dashboard currently shown in
   *  the published view. The dashboard is already published here, so this only
   *  surfaces the `/dynamic-dashboard/<slug>` link (Copy / Open preview) — no
   *  re-publish. Mirrors the dialog the builder's old Share button opened. */
  shareThisDashboard(): void {
    const slug = this.route.snapshot.paramMap.get('slug') || this.sourceKey?.replace(/^self-service:/, '') || '';
    if (!slug) return;

    // Pass the SAME layout JSON that renders this dashboard (the untouched config
    // held from the load) so "Give a user access" inserts the real layout under
    // the recipient's id — identical to what the publish button sends. The dialog
    // only falls back to a slug lookup when this is absent.
    const layoutJson =
      (this.originalDashboardConfig && typeof this.originalDashboardConfig === 'object')
        ? (this.originalDashboardConfig as Record<string, unknown>)
        : undefined;
    const dashboardName =
      this.resolveDashboardTitle(this.originalDashboardConfig) ||
      String((layoutJson?.['name'] as string) ?? '') ||
      slug;

    this.dialog.open(ShareDashboardDialogComponent, {
      width: 'min(560px, 94vw)',
      maxWidth: '94vw',
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'ss-share-dialog-panel',
      data: {
        // `shared=1` forces the recipient's read-only view (Edit Layout only)
        // even if the person opening the link happens to be the creator.
        shareLink: `${window.location.origin}/dynamic-dashboard/${slug}?shared=1`,
        shareSlug: slug,
        layoutJson,
        dashboardName,
      },
    });
  }

  backToSelfServiceBuilder(): void {
    const dashboardSlug = this.route.snapshot.paramMap.get('slug') || this.sourceKey?.replace(/^self-service:/, '') || '';
    const dashboardId = this.getSelfServiceDashboardId(dashboardSlug);
    const isDvmLayoutDashboard = this.configurations?.['builder'] === 'dvm-layout';
    const view = isDvmLayoutDashboard ? 'layout' : 'dashboards';
    const route = isDvmLayoutDashboard ? '/dvm-layout-builder' : '/generate-visualization';

    this.router.navigate([route], {
      queryParams: {
        view,
        dashboard: dashboardSlug || null,
        dashboardId: dashboardId || null,
        preview: 'true',
      },
      state: {
        selfServiceView: view,
        dashboardSlug,
        dashboardId,
        preview: true,
      },
    });
  }

  private getSelfServiceDashboardId(slug: string): string {
    try {
      // readJson decompresses the (now compressed) savedDashboards payload; the
      // builder writes it via the same codec. A raw JSON.parse here would throw
      // on the compressed string and silently return '' (no dashboard found).
      const dashboards = readJson<Array<Record<string, unknown>>>(
        DashboardComponent.SELF_SERVICE_DASHBOARDS_STORAGE_KEY,
        [],
      );
      const target = (slug || '').toLowerCase();
      const match = dashboards.find(
        (d) => DashboardComponent.dashboardNameToSlug(String(d?.['name'] ?? '')) === target
      );
      return String(match?.['id'] ?? '');
    } catch {
      return '';
    }
  }

  ngOnInit(): void {
    // Ensure the self-service API base URL is set for the PUBLISHED-DASHBOARD
    // VIEWER. Normally the builders set it when their JSON config loads
    // (SelfServiceApiConfigService.applyFromLayout), but opening a published
    // dashboard directly at /dynamic-dashboard/<slug> never mounts a builder, so
    // getSelfServiceBaseUrl() stayed empty. That made getPublishedLayoutBySlug()
    // POST to a relative URL -> 404 -> dashboards published on ANOTHER machine
    // (present only on the API, not this browser's localStorage) rendered blank.
    // Seed it from environment.URL (the same base the sidenav already uses for
    // its dynamic_filter call), but don't clobber a value a builder already set.
    if (!getSelfServiceBaseUrl() && selfServiceBaseUrl()) {
      setSelfServiceApiOverrides({ baseUrl: selfServiceBaseUrl() });
    }

    this.responsiveLayoutService.init();

    this.subs.add(
      this.responsiveLayoutService.reflow$.subscribe(() => {
        if (this.configurations?.pageContent?.length) {
          this.applyWidgetHeightsInPlace(this.configurations);
        }
        this.queueCompactFitMeasurement();
      })
    );

    // Load the layout manifest in parallel with the dashboard config — once
    // it arrives, re-resolve the active layout so a saved selection (from
    // localStorage) is honored on first paint, not just after the user picks.
    this.subs.add(
      this.layoutService.loadManifest().subscribe(() => this.maybeReapplyLayout())
    );

    // React to layout switches from the Theme Options dialog: when the user
    // hits "Apply & Preview" or "Clear Layout", reshape the current dashboard
    // and re-run the grid pipeline. The first emission (BehaviorSubject seed)
    // is a no-op because originalDashboardConfig isn't loaded yet.
    this.subs.add(
      this.layoutService.activeLayoutId$.subscribe(() => this.maybeReapplyLayout())
    );

    // Seed filters from navigation before any widget API call starts.
    this.globalFilterService.updateAllFilters(this.buildInitialFiltersFromNavigation());
    this.globalFilterService.activeFilter = null;
    this.globalFilterService.resetFilterSelection('customer_spec');

    this.subs.add(
      combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(([params, queryParams]) => {
        const data = this.route.snapshot.data;

        // A tab switch only mutates the `?tab=` query param — the dashboard's
        // route identity (slug / configFile) is unchanged. When that identity
        // is the same as what's already loaded, re-resolve the active tab in
        // place instead of re-fetching the whole config (which would reset
        // filter state). Guarding on route identity (not just "config loaded")
        // ensures navigation to a *different* dashboard still reloads.
        const routeIdentity = this.buildRouteIdentity(params, data);
        if (
          this.originalDashboardConfig &&
          this.hasTabs &&
          routeIdentity === this.loadedRouteIdentity
        ) {
          const urlTab = queryParams.get('tab');
          const matched = this.findTabByUrlKey(this.tabs, urlTab);
          if (matched && matched.id !== this.activeTabId) {
            this.activeTabId = matched.id;
            this.maybeReapplyLayout();
            requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
          }
          return;
        }
        this.loadedRouteIdentity = routeIdentity;

        const inputConfigFile = (this.configFile || '').trim();
        const generatedSlug = this.generatedDashboardSlug(params.get('dashboardSlug'));
        const generatedConfigFile = generatedSlug ? this.generatedDashboardConfigFile(generatedSlug) : null;
        const nextConfigFile = inputConfigFile || generatedConfigFile || data['configFile'];

        // Dynamic dashboard fetched from the layout API and cached in localStorage.
        if (data['configSource'] === 'dynamic-api' && !inputConfigFile) {
          const slug = params.get('slug') || '';
          this.sourceKey = `dynamic-api:${slug}`;
          this.isSelfServicePublished = true;

          // A `shared=1` flag on the URL is the Share link / "Open preview" —
          // it must ALWAYS render the recipient's read-only view (Edit Layout
          // only, no Back-to-Builder/Share), even when the creator opens it.
          // This overrides the ownership check below.
          const isSharedLink = queryParams.get('shared') === '1';
          if (isSharedLink) {
            this.isSharedReadonlyView = true;
            this.loadDynamicApiDashboard(slug);
            return;
          }

          // The creator and a shared recipient reach this SAME route. Only a
          // genuine recipient is a "shared read-only view" (hides Back to Builder
          // AND Share). The creator viewing their own dashboard keeps the Share
          // action.
          //
          // Fast path: local saved dashboards give an instant owner answer when
          // present. But logout runs localStorage.clear(), so after a fresh
          // login that key is empty — start read-only and CONFIRM ownership via
          // the email-filtered API (logout-proof), flipping to owner-view (Share
          // shown) when it comes back true.
          this.isSharedReadonlyView = !this.ownsSelfServiceDashboard(slug);
          if (this.isSharedReadonlyView) {
            this.confirmDynamicDashboardOwnership(slug);
          }
          this.loadDynamicApiDashboard(slug);
          return;
        }

        // Published self-service dashboards are served from the :slug route with
        // data.configSource === 'self-service-local'. The config lives in
        // localStorage (no backend), so it's read by name-slug rather than a file.
        if (data['configSource'] === 'self-service-local' && !inputConfigFile) {
          const slug = params.get('slug') || '';
          this.sourceKey = `self-service:${slug}`;
          this.isSelfServicePublished = true;
          // Creator's own publish view — "Back to Builder" applies here.
          this.isSharedReadonlyView = false;
          this.loadSelfServicePublishedDashboard(slug);
          return;
        }

        if (generatedSlug && !inputConfigFile) {
          this.configFileName = generatedConfigFile!;
          this.sourceKey = this.dashboardSourceKey || this.configFileName;
          this.loadGeneratedDashboard(generatedSlug, this.configFileName);
          return;
        }

        if (nextConfigFile) {
          if (this.configFileName === nextConfigFile && this.originalDashboardConfig) {
            return;
          }
          this.configFileName = nextConfigFile;
          this.sourceKey = this.dashboardSourceKey || this.configFileName;
          this.loadCharts();
          return;
        }

        const appId = this.route.snapshot.data['dvmWorkflowAppId'];
        if (appId) {
          this.sourceKey = appId;
          this.loadDashboardFromApi(environment.PROJECT_ID, appId);
        }
      })
    );
  }

  /**
   * A stable key identifying *which* dashboard the current route resolves to,
   * independent of the `?tab=` query param. Any change here means we navigated
   * to a different dashboard and must reload; an unchanged value with only a
   * different `tab` means an in-place tab switch.
   */
  private buildRouteIdentity(params: any, data: any): string {
    return JSON.stringify({
      configSource: data?.['configSource'] ?? null,
      configFile: data?.['configFile'] ?? null,
      appId: data?.['dvmWorkflowAppId'] ?? null,
      inputConfigFile: (this.configFile || '').trim() || null,
      slug: params?.get?.('slug') ?? null,
      dashboardSlug: params?.get?.('dashboardSlug') ?? null,
    });
  }

  private generatedDashboardSlug(dashboardSlug: string | null): string | null {
    const slug = (dashboardSlug || '').trim();
    if (!/^[A-Za-z0-9_-]+$/.test(slug)) {
      return null;
    }
    return slug;
  }

  private generatedDashboardConfigFile(dashboardSlug: string | null): string | null {
    const slug = this.generatedDashboardSlug(dashboardSlug);
    if (!slug) return null;
    return `generated/${slug}.json`;
  }

  private static readonly SELF_SERVICE_DASHBOARDS_STORAGE_KEY = 'dvm.selfService.savedDashboards';
  private static readonly DYNAMIC_DASHBOARD_STORAGE_KEY = 'dvm.dynamicDashboard';

  private loadSelfServicePublishedDashboard(slug: string): void {
    const config = this.readSelfServicePublishedConfig(slug);
    if (!config) {
      console.error(`[Published dashboard] No saved dashboard with slug "${slug}".`);
      return;
    }

    const themeName =
      this.themeService.getSavedThemeName() ||
      this.themeService.currentThemeName ||
      this.themeService.getUserPreference() ||
      (config as any)?.theme || (environment as any).THEME_NAME || 'default';

    const configSub = this.themeService.loadTheme(themeName).pipe(
      tap((theme) => {
        this.themeService.initFromLoaded(themeName, theme);
        if ((config as any)?.themeOverrides) {
          this.themeService.setDashboardOverrides((config as any).themeOverrides);
        }
      }),
      switchMap(() => this.roleVisibilityService.loadMapping().pipe(
        switchMap(() => this.loadPersistedLayouts(config).pipe(
          map((persistedLayouts) => ({ response: config, persistedLayouts })),
        )),
      )),
    ).subscribe(({ response, persistedLayouts }) => {
      this.persistedLayouts = persistedLayouts;
      this.originalDashboardConfig = response;
      this.layoutService.setActiveDashboardKey(
        this.sourceKey,
        (response as any)?.layoutId ?? null,
      );
      this.applyDashboardWithLayout(response);
      // Land at the top so the first row of charts is visible — the builder
      // page may have been scrolled down when "Publish" was clicked, and that
      // scroll position otherwise carries over into the published view.
      this.scrollPublishedViewToTop();
    });

    this.subs.add(configSub);
  }

  private loadDynamicApiDashboard(slug = ''): void {
    const readCachedConfig = (): Record<string, unknown> | null => {
      // Compressed cache (readDynamicDashboardCache also reads legacy plaintext).
      // The legacy single-key path (no slug) has no compressed equivalent, so
      // fall back to a direct read for it.
      if (slug) {
        const cached = readDynamicDashboardCache<Record<string, unknown> | null>(slug, null);
        return cached && typeof cached === 'object' ? cached : null;
      }
      try {
        const raw = window.localStorage.getItem(DashboardComponent.DYNAMIC_DASHBOARD_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    };

    // API is the source of truth for the published charts. Previously this
    // rendered the localStorage copy first and returned early on a cache hit,
    // so the creator (whose machine holds the cache) saw their stale local
    // charts render, then flip to the API version ~5-6s later once the
    // sidenav's poll overwrote the cache. We now fetch by slug from the API
    // (email-independent) and render THAT, refreshing the cache from it. The
    // localStorage cache is used only as a fallback when the API is
    // unreachable or returns nothing — which also keeps offline/dev working.
    if (!slug) {
      // No slug to fetch — the cache is all we have (legacy single-key path).
      const cached = readCachedConfig();
      if (cached) {
        this.renderDynamicApiConfig(cached);
      } else {
        console.error('[Dynamic dashboard] No cached config and no slug to fetch.');
      }
      return;
    }

    // Pass the current user's email so the fetch can use the email-filtered
    // `dynamic_filter` endpoint (the empty-email `select` returns nothing on this
    // backend). This is what lets dashboards published on ANOTHER machine — which
    // exist only on the API, not this browser's localStorage — actually resolve.
    //
    // IMPORTANT: the email must be RESOLVED (async) before the fetch. In a fresh
    // session (e.g. Incognito) the Keycloak token may not be parsed yet during
    // ngOnInit, so the synchronous getCurrentLayoutUserContext().email is '' —
    // which made the fetch fall back to the empty-email `select` (COMMIT_ROLLBACK
    // → nothing → "not found"). resolveCurrentEmail() awaits loadUserProfile()
    // when the token isn't ready, matching how the sidenav gets its email.
    // Cache-first paint (perf): if the sidenav already cached THIS slug's layout,
    // render it immediately so charts appear with ~no delay, then refresh from
    // the API in the background. Safe because:
    //   • the cache is keyed by the exact slug (no cross-dashboard/user mixing),
    //   • the API still runs and is authoritative — we swap to it,
    //   • we only RE-render when the API result actually DIFFERS from what we
    //     painted, so an unchanged dashboard shows no flash / no double-render.
    // This restores instant paint WITHOUT the stale-forever bug the old
    // cache-and-return had (that returned early and never hit the API).
    const cachedConfig = readCachedConfig();
    let paintedSignature: string | null = null;
    if (cachedConfig) {
      // Snapshot the signature BEFORE rendering — renderDynamicApiConfig can
      // mutate the config object (applyDashboardWithLayout), which would make
      // the post-render signature differ from the API's even when nothing
      // changed, forcing a redundant re-render/flash on every open.
      paintedSignature = this.dashboardConfigSignature(cachedConfig);
      this.renderDynamicApiConfig(cachedConfig);
    }

    this.resolveCurrentEmail().then((currentEmail) => {
      this.subs.add(
        this.selfServiceDashboardService.getPublishedLayoutBySlug(slug, currentEmail).subscribe((fetched) => {
          if (fetched) {
            // Compressed + LRU-capped cache (was raw JSON.stringify, which — one
            // uncapped key per dashboard — grew to ~10MB and exhausted the quota).
            writeDynamicDashboardCache(slug, fetched);
            // Only re-render if we haven't painted, or the API config differs from
            // the cached one we already painted (avoids a redundant re-render/flash).
            if (!cachedConfig || this.dashboardConfigSignature(fetched) !== paintedSignature) {
              this.renderDynamicApiConfig(fetched);
            }
            return;
          }

          // API failed / returned nothing. If we already painted from cache,
          // keep it. Otherwise try the cache now as a last resort.
          if (cachedConfig) return;
          const cached = readCachedConfig();
          if (cached) {
            this.renderDynamicApiConfig(cached);
          } else {
            console.error(`[Dynamic dashboard] No published dashboard found for slug "${slug}".`);
          }
        }),
      );
    });
  }

  /** Change-signature for a dashboard config, used to decide whether an API
   *  result differs from an already-painted cached one. Must reflect the actual
   *  CHART CONTENT — not just row/tab counts — otherwise removing (or swapping)
   *  a chart WITHIN a row leaves the signature unchanged, the API result is
   *  judged "same as cache", the re-render is skipped, and the stale (removed)
   *  chart keeps showing. So we walk every widget in every row/tab and fold its
   *  identity (id + type + title + span) into the signature. Not a strict hash —
   *  just good enough to skip no-op re-renders while catching any real change. */
  private dashboardConfigSignature(config: Record<string, unknown>): string {
    try {
      const publishedAt = String((config as any)?.['publishedAt'] ?? '');
      const name = String((config as any)?.['name'] ?? (config as any)?.['dashboard-name'] ?? '');
      // Collect every widget across the flat pageContent AND any tabs. A tab may
      // carry its own `pageContent`, so we recurse into both shapes.
      const widgets: string[] = [];
      const collectFromPageContent = (pageContent: unknown): void => {
        if (!Array.isArray(pageContent)) return;
        pageContent.forEach((row: any) => {
          const columns = row?.columns;
          if (!Array.isArray(columns)) return;
          columns.forEach((widget: any) => {
            const id = String(widget?.widgetId ?? widget?.componentId ?? '');
            const type = String(widget?.type ?? '');
            const title = String(widget?.title ?? '');
            const span = String(widget?.layout?.colSpan ?? '') + 'x' + String(widget?.layout?.rowSpan ?? '');
            widgets.push(`${id}:${type}:${title}:${span}:${this.stableConfigSignature(widget)}`);
          });
        });
      };
      collectFromPageContent((config as any)?.['pageContent']);
      const tabs = (config as any)?.['tabs'];
      if (Array.isArray(tabs)) {
        tabs.forEach((tab: any) => collectFromPageContent(tab?.pageContent));
      }
      return `${name}|${publishedAt}|${widgets.length}|${widgets.join('~')}`;
    } catch {
      return '';
    }
  }

  private stableConfigSignature(value: unknown): string {
    const seen = new WeakSet<object>();
    const normalize = (item: unknown): unknown => {
      if (item === null || item === undefined) return item ?? null;
      if (typeof item === 'function') return undefined;
      if (typeof item !== 'object') return item;
      if (seen.has(item as object)) return '[Circular]';
      seen.add(item as object);

      if (Array.isArray(item)) {
        return item.map((entry) => normalize(entry));
      }

      return Object.keys(item as Record<string, unknown>)
        .sort()
        .reduce((acc, key) => {
          const normalized = normalize((item as Record<string, unknown>)[key]);
          if (normalized !== undefined) acc[key] = normalized;
          return acc;
        }, {} as Record<string, unknown>);
    };

    return JSON.stringify(normalize(value));
  }
  /** Resolve the current user's email for the dashboard-ownership API. Prefers
   *  the Keycloak ACCOUNT PROFILE email (loadUserProfile) — the SAME source the
   *  sidenav uses (getCurrentUserEmail → userEmail set from loadUserProfile).
   *
   *  This matters: the JWT `email` CLAIM can differ from the account profile
   *  email (e.g. token claim = spriced-restart@simadvisory.com but the account is
   *  aditya.b@simadvisorytech.com). The dashboards are owned under the PROFILE
   *  email, so the sidenav (profile email) listed 38 dashboards while the viewer
   *  (token-claim email) got 0 — every open said "not found". Using the profile
   *  email here makes the viewer's fetch match the sidenav's list. Falls back to
   *  the token claim only if the profile can't be loaded. */
  private async resolveCurrentEmail(): Promise<string> {
    // Memoized: resolved once per session, then reused on every navigation so we
    // don't pay the loadUserProfile() round-trip each time a dashboard opens.
    if (this.resolvedEmailMemo) return this.resolvedEmailMemo;
    let email = '';
    try {
      if (await this.keycloakService.isLoggedIn()) {
        const profile = await this.keycloakService.loadUserProfile();
        email = profile.email || '';
      }
    } catch { /* profile unavailable — fall back to the token claim below */ }
    if (!email) email = this.getCurrentLayoutUserContext().email || '';
    if (email) this.resolvedEmailMemo = email;
    return email;
  }

  /** Render a resolved dynamic-dashboard config (from cache or API). */
  private renderDynamicApiConfig(config: Record<string, unknown>): void {
    const themeName =
      this.themeService.getSavedThemeName() ||
      this.themeService.currentThemeName ||
      this.themeService.getUserPreference() ||
      (config as any)?.theme ||
      (environment as any).THEME_NAME ||
      'default';

    const configSub = this.themeService.loadTheme(themeName).pipe(
      tap((theme) => {
        this.themeService.initFromLoaded(themeName, theme);
        if ((config as any)?.themeOverrides) {
          this.themeService.setDashboardOverrides((config as any).themeOverrides);
        }
      }),
      switchMap(() =>
        this.roleVisibilityService.loadMapping().pipe(
          switchMap(() =>
            this.loadPersistedLayouts(config!).pipe(
              map((persistedLayouts) => ({ response: config!, persistedLayouts })),
            ),
          ),
        ),
      ),
    ).subscribe(({ response, persistedLayouts }) => {
      this.persistedLayouts = persistedLayouts;
      this.originalDashboardConfig = response;
      this.layoutService.setActiveDashboardKey(this.sourceKey, (response as any)?.layoutId ?? null);
      this.applyDashboardWithLayout(response);
      this.scrollPublishedViewToTop();
    });

    this.subs.add(configSub);
  }

  /** Scroll the published dashboard back to the top after it renders. Defers a
   *  frame so the grid has laid out before we reset the scroll position. */
  private scrollPublishedViewToTop(): void {
    setTimeout(() => {
      const hostEl = this.hostRef?.nativeElement;
      const viewportHost = hostEl?.closest(
        'mat-sidenav-content, .mat-sidenav-content',
      ) as HTMLElement | null;
      viewportHost?.scrollTo({ top: 0, left: 0 });
      this.dashboardContentRef?.nativeElement?.scrollTo?.({ top: 0, left: 0 });
      window.scrollTo({ top: 0, left: 0 });
    });
  }

  /** True when the current user is the creator of the dashboard with this slug.
   *  Two signals, either sufficient:
   *   1) The sidenav's owned-slug registry (populated from the email-filtered
   *      `dynamic_filter` API on every login) — LOGOUT-PROOF and covers
   *      dashboards published in a PREVIOUS session that aren't in this
   *      browser's localStorage. This is what makes "Back to Builder" + "Share"
   *      show for already-published dashboards opened from the sidebar.
   *   2) The self-service saved dashboards in localStorage — covers the same
   *      session before the sidenav's API responds, and offline/dev.
   *  Either way, a genuine share-link recipient has neither, so they stay
   *  read-only. */
  private ownsSelfServiceDashboard(slug: string): boolean {
    const target = (slug || '').toLowerCase();
    if (!target) return false;
    if (this.selfServiceDashboardService.isOwnedDashboardSlug(target)) return true;
    try {
      const dashboards = readJson<Array<Record<string, unknown>>>(
        DashboardComponent.SELF_SERVICE_DASHBOARDS_STORAGE_KEY,
        [],
      );
      return (
        Array.isArray(dashboards) &&
        dashboards.some(
          (d) => DashboardComponent.dashboardNameToSlug(String(d?.['name'] ?? '')) === target,
        )
      );
    } catch {
      return false;
    }
  }

  /** Confirm ownership of a dynamic-api dashboard when the synchronous fast-path
   *  couldn't (e.g. opened by direct URL / hard refresh before the sidenav's
   *  owned-slug registry filled, or after a fresh login cleared localStorage).
   *  Two logout-proof fallbacks:
   *   1) Poll the sidenav registry for a short window — it fills from the
   *      email-filtered API the sidenav runs on every login. Reliable and needs
   *      no re-derived email.
   *   2) In parallel, ask the API directly with the current user's email.
   *  Either returning true clears the read-only flag (owner regains Share).
   *  Only ever RELAXES the flag (recipient → owner); never locks an owner down. */
  private confirmDynamicDashboardOwnership(slug: string): void {
    // 1) Poll the shared owned-slug registry (populated by the sidenav's login
    //    fetch). Give it up to ~5s in case the sidenav's API is slow.
    let attempts = 0;
    const poll = setInterval(() => {
      attempts += 1;
      if (this.selfServiceDashboardService.isOwnedDashboardSlug(slug)) {
        this.isSharedReadonlyView = false;
        clearInterval(poll);
      } else if (attempts >= 10 || !this.isSharedReadonlyView) {
        clearInterval(poll);
      }
    }, 500);
    this.subs.add({ unsubscribe: () => clearInterval(poll) });

    // 2) Direct API confirmation with the user's email (independent of the
    //    sidenav). Harmless if it loses the race with the poll.
    const email = this.getCurrentLayoutUserContext().email || '';
    if (!email) return;
    this.subs.add(
      this.selfServiceDashboardService
        .isDashboardOwnedByCurrentUser(slug, email)
        .subscribe((owned) => {
          if (owned) this.isSharedReadonlyView = false;
        }),
    );
  }

  private readSelfServicePublishedConfig(slug: string): Record<string, unknown> | null {
    try {
      // Published-dashboard render path. Reads the builder's savedDashboards,
      // which are now stored compressed — readJson decompresses transparently.
      // (A raw JSON.parse threw on the compressed string, returning null here and
      // rendering a blank published dashboard with no charts.)
      const dashboards = readJson<Array<Record<string, unknown>>>(
        DashboardComponent.SELF_SERVICE_DASHBOARDS_STORAGE_KEY,
        [],
      );
      if (!Array.isArray(dashboards)) return null;
      const target = (slug || '').toLowerCase();
      const match = dashboards.find((d) => {
        if (!d) return false;
        const name = String((d as any).name ?? '');
        return DashboardComponent.dashboardNameToSlug(name) === target;
      });
      const config = match?.['config'];
      return config && typeof config === 'object' ? (config as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }

  static dashboardNameToSlug(name: string): string {
    return String(name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  ngAfterViewInit(): void {
    this.responsiveLayoutService.startChartAutoResize();
    this.gridObserver = new ResizeObserver(() => {
      this.onViewportChange();
      this.queueCompactFitMeasurement();
    });
    if (this.dashboardContentRef?.nativeElement) this.gridObserver.observe(this.dashboardContentRef.nativeElement);
    if (this.gridContainerRef?.nativeElement) this.gridObserver.observe(this.gridContainerRef.nativeElement);
    this.onViewportChange();
    this.queueCompactFitMeasurement();
  }

  ngOnDestroy(): void {
    this.finishInteraction(false);
    this.gridObserver?.disconnect();
    if (this.compactFitRafId !== null) {
      cancelAnimationFrame(this.compactFitRafId);
      this.compactFitRafId = null;
    }

    // ✅ from other branch
    this.themeService.clearDashboardOverrides();

    this.responsiveLayoutService.stopChartAutoResize();
    this.subs.unsubscribe();

    this.stopFocusTooltipSync();
    void this.exitFocusMode();
    this.syncFocusBodyClass();

    this.responsiveLayoutService.setScreenFloating(true);
    this.responsiveLayoutService.setCompactLayoutFitOverride(null);
    this.compactScrollLock = false;
    this.compactScrollLockSnapshot = null;

    this.globalFilterService.clearAllFilters();
    this.globalFilterService.activeFilter = null;
    this.dashboardTitleService.clearDashboardTitle();
  }

  /**
   * Seed GlobalFilterService from navigation query params + router state payload.
   * This enables cross-page payload handoff (e.g. navigateAction.stateKey payload)
   * without requiring each destination widget/component to read Router state.
   */
  private buildInitialFiltersFromNavigation(): {
    queryParams: Record<string, any>;
    requestParams: Record<string, any>;
    widgetId: string | null;
    filterListener?: string[];
    sourceWidgetId?: string | null;
  } {
    const queryParams = { ...(this.route?.snapshot?.queryParams || {}) } as Record<string, any>;
    const requestParams: Record<string, any> = {};
    const navState: any = (window.history?.state || {}) as any;
    let filterListener: string[] | undefined;
    let sourceWidgetId: string | null = null;

    // Common pattern used in dashboard configs:
    // navigateAction: { stateKey: "crossNavPayload", payload: { requestData: {...} } }
    const crossNavPayload = navState?.crossNavPayload;
    if (crossNavPayload && typeof crossNavPayload === 'object') {
      if (crossNavPayload.requestData && typeof crossNavPayload.requestData === 'object') {
        Object.assign(requestParams, crossNavPayload.requestData);
      }
      if (crossNavPayload.queryParams && typeof crossNavPayload.queryParams === 'object') {
        Object.assign(queryParams, crossNavPayload.queryParams);
      }
      if (crossNavPayload.requestParams && typeof crossNavPayload.requestParams === 'object') {
        Object.assign(requestParams, crossNavPayload.requestParams);
      }
      Object.assign(requestParams, this.extractLooseRequestParams(crossNavPayload));
      filterListener = this.normalizeNavigationListener(
        crossNavPayload.filterListener ??
        crossNavPayload.listenerWidget ??
        crossNavPayload.listenerWidgets ??
        crossNavPayload.ListenerWidget ??
        crossNavPayload.ListenerWidgets ??
        crossNavPayload.listnerWidget ??
        crossNavPayload.listnerWidgets ??
        crossNavPayload.ListnerWidget ??
        crossNavPayload.ListnerWidgets ??
        crossNavPayload.Listnerwidget,
      );
      sourceWidgetId = this.firstString(crossNavPayload.sourceWidgetId, queryParams['sourceWidgetId']);
    }

    // Also support direct state usage for flexibility.
    if (navState?.requestParams && typeof navState.requestParams === 'object') {
      Object.assign(requestParams, navState.requestParams);
    }
    if (navState?.queryParams && typeof navState.queryParams === 'object') {
      Object.assign(queryParams, navState.queryParams);
    }
    const directListener = this.normalizeNavigationListener(
      navState?.filterListener ??
      navState?.listenerWidget ??
      navState?.listenerWidgets ??
      navState?.ListenerWidget ??
      navState?.ListenerWidgets ??
      navState?.listnerWidget ??
      navState?.listnerWidgets ??
      navState?.ListnerWidget ??
      navState?.ListnerWidgets ??
      navState?.Listnerwidget,
    );
    if (directListener?.length) {
      filterListener = directListener;
    }
    sourceWidgetId = this.firstString(sourceWidgetId, navState?.sourceWidgetId, queryParams['sourceWidgetId']);

    return {
      queryParams,
      requestParams,
      widgetId: null,
      ...(filterListener?.length ? { filterListener } : {}),
      ...(sourceWidgetId ? { sourceWidgetId } : {}),
    };
  }

  private normalizeNavigationListener(rawValue: unknown): string[] | undefined {
    const rawList = Array.isArray(rawValue)
      ? rawValue
      : typeof rawValue === 'string'
        ? rawValue.split(',')
        : [];

    const listeners = rawList
      .map((item) => String(item || '').trim())
      .filter((item) => !!item);

    return listeners.length ? listeners : undefined;
  }

  private extractLooseRequestParams(payload: any): Record<string, any> {
    if (!payload || typeof payload !== 'object') return {};

    const hasStructuredRequestParams =
      (payload.requestData && typeof payload.requestData === 'object') ||
      (payload.requestParams && typeof payload.requestParams === 'object');

    if (hasStructuredRequestParams) return {};

    const reservedKeys = new Set([
      'queryParams',
      'requestParams',
      'requestData',
      'filterListener',
      'listenerWidget',
      'listenerWidgets',
      'ListenerWidget',
      'ListenerWidgets',
      'listnerWidget',
      'listnerWidgets',
      'ListnerWidget',
      'ListnerWidgets',
      'Listnerwidget',
      'sourceWidgetId',
    ]);
    const looseParams: Record<string, any> = {};

    for (const [key, value] of Object.entries(payload)) {
      if (reservedKeys.has(key) || value === undefined || value === null || value === '') {
        continue;
      }
      looseParams[key] = value;
    }

    return looseParams;
  }

  private firstString(...values: unknown[]): string | null {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return null;
  }

  private resolveDashboardTitle(config: any): string {
    return String(
      config?.['dashboard-name'] ??
      config?.dashboardName ??
      config?.dashboard_name ??
      ''
    ).trim();
  }

  private setDashboardTitleContext(config: any): void {
    this.dashboardTitleService.setDashboardTitle(
      this.resolveDashboardTitle(config),
      'top',
      this.normalizeDashboardNameFontSize(
        config?.dashboardNameFontSize ??
        config?.['dashboard-name-font-size'] ??
        config?.['dashboardNamefontsize'] ??
        config?.['dashboardNAmefontsize']
      ),
    );
  }

  private normalizeDashboardNameFontSize(value: unknown): string {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return `${value}rem`;
    }

    if (typeof value !== 'string') return '';

    const fontSize = value.trim();
    if (!fontSize || /\bpx\b/i.test(fontSize)) return '';

    return fontSize;
  }

  loadDashboardFromApi(projectId: string, appId: string) {
    const configSub = this.platformService
      .getDashboardConfigFromApi(projectId, appId)
      .pipe(
        switchMap((response: any) => {
          if (!response?.length) {
            return of({ config: null, persistedLayouts: null });
          }

          const config = response[0].dvm_workflow_map;
          // Priority: user's saved active theme (Save Theme button) > current
          // in-memory theme (covers a still-pending preview not yet saved) >
          // light/dark toggle > dashboard config > env default > 'default'.
          // Without the saved-name check, navigating away and back would
          // revert a saved theme to the dashboard JSON's `theme` field —
          // because `getUserPreference()` reads `dvm.theme` (light/dark
          // toggle), not `dvm.theme.activeName` (Save Theme target).
          const themeName =
            this.themeService.getSavedThemeName() ||
            this.themeService.currentThemeName ||
            this.themeService.getUserPreference() ||
            config?.theme || (environment as any).THEME_NAME || 'default';

          return this.themeService.loadTheme(themeName).pipe(
            tap((theme) => {
              this.themeService.initFromLoaded(themeName, theme);

              if (config?.themeOverrides) {
                this.themeService.setDashboardOverrides(config.themeOverrides);
              }
            }),
            switchMap(() => this.roleVisibilityService.loadMapping().pipe(
              switchMap(() =>
              this.loadPersistedLayouts(config).pipe(
                map((persistedLayouts) => ({ config, persistedLayouts }))
              )
            )
            ))
          );
        })
      )
      .subscribe({
        next: ({ config, persistedLayouts }: { config: any; persistedLayouts: Record<string, GridItem[]> | null }) => {
          if (!config) return;
          this.persistedLayouts = persistedLayouts;
          this.originalDashboardConfig = config;
          // Per-dashboard layout: tell the service which dashboard is active
          // (sourceKey = appId here) and what its JSON-declared default is,
          // so the resolved layout reflects this dashboard's saved pick or
          // its `layoutId` field. Must precede applyDashboardWithLayout —
          // the resulting activeLayoutId$ emission also fires the layout
          // subscription, but the signature check makes the second pass a
          // no-op.
          this.layoutService.setActiveDashboardKey(
            this.sourceKey,
            config?.layoutId ?? null,
          );
          this.applyDashboardWithLayout(config);
        },
        error: (err) =>
          console.error('Error loading dashboard config:', err),
      });

    this.subs.add(configSub); // ✅ fixed
  }

  loadGeneratedDashboard(dashboardSlug: string, fallbackConfigFile: string) {
    const configSub = this.configService
      .getGeneratedDashboardConfig(dashboardSlug)
      .pipe(
        catchError((err) => {
          console.warn('Generated dashboard API unavailable; falling back to static dashboard config:', err);
          return this.configService.getDashboardConfig(fallbackConfigFile);
        }),
        switchMap((response: any) => {
          const themeName =
            this.themeService.getSavedThemeName() ||
            this.themeService.currentThemeName ||
            this.themeService.getUserPreference() ||
            response?.theme || (environment as any).THEME_NAME || 'default';

          return this.themeService.loadTheme(themeName).pipe(
            tap((theme) => {
              this.themeService.initFromLoaded(themeName, theme);

              if (response?.themeOverrides) {
                this.themeService.setDashboardOverrides(
                  response.themeOverrides
                );
              }
            }),
            switchMap(() => of(response))
          );
        })
      )
      .subscribe((response) => {
        this.originalDashboardConfig = response;
        this.layoutService.setActiveDashboardKey(
          this.sourceKey,
          response?.layoutId ?? null,
        );
        this.applyDashboardWithLayout(response);
      });

    this.subs.add(configSub);
  }

  loadCharts() {
    const configSub = this.configService
      .getDashboardConfig(this.configFileName)
      .pipe(
        switchMap((response: any) => {
          // Priority: user's saved active theme (Save Theme button) > current
          // in-memory theme (covers a still-pending preview not yet saved) >
          // light/dark toggle > dashboard config > env default > 'default'.
          const themeName =
            this.themeService.getSavedThemeName() ||
            this.themeService.currentThemeName ||
            this.themeService.getUserPreference() ||
            response?.theme || (environment as any).THEME_NAME || 'default';

          return this.themeService.loadTheme(themeName).pipe(
            tap((theme) => {
              this.themeService.initFromLoaded(themeName, theme);

              if (response?.themeOverrides) {
                this.themeService.setDashboardOverrides(
                  response.themeOverrides
                );
              }
            }),
            switchMap(() => this.roleVisibilityService.loadMapping().pipe(
              switchMap(() =>
              this.loadPersistedLayouts(response).pipe(
                map((persistedLayouts) => ({ response, persistedLayouts }))
              )
            )
            ))
          );
        })
      )
      .subscribe(({ response, persistedLayouts }) => {
        this.persistedLayouts = persistedLayouts;
        this.originalDashboardConfig = response;
        // Per-dashboard layout — see loadDashboardFromApi for the rationale.
        this.layoutService.setActiveDashboardKey(
          this.sourceKey,
          response?.layoutId ?? null,
        );
        this.applyDashboardWithLayout(response);
      });

    this.subs.add(configSub); //  fixed
  }

  /**
   * Resolve the effective dashboard config (reshape via active Custom Layout
   * if one is selected, else use the dashboard's own pageContent), then run
   * the height + screen-floating + grid + responsive pipeline.
   *
   * Skips the work if the resolved layout signature matches what's already
   * applied — avoids unnecessary chart re-renders on benign emissions
   * (BehaviorSubject seed, manifest-loaded callback firing identical state).
   */
  /**
   * Inspect the dashboard config for a `tabs` array. When present, populate
   * `this.tabs`, resolve the active tab (from the `?tab=` query param, falling
   * back to the first tab), and return a *flattened* config whose `pageContent`
   * is the active tab's widgets. The rest of the pipeline then treats a tabbed
   * dashboard exactly like a single-page one.
   *
   * For classic dashboards (no `tabs`) this clears tab state and returns the
   * config untouched.
   */
  private resolveActiveTabConfig(config: any): any {
    const rawTabs = Array.isArray(config?.tabs) ? config.tabs : [];
    const usedSlugs = new Set<string>();
    const tabs: TabDef[] = rawTabs
      .filter((t: any) => t && (t.id != null))
      .map((t: any, i: number) => {
        const label = String(t.label ?? t.title ?? `Tab ${i + 1}`);
        // Prefer an explicit slug on the config; else derive a clean, unique one
        // from the label. Uniqueness guards against two tabs with the same name.
        let slug = String(t.slug ?? DashboardComponent.dashboardNameToSlug(label)) || `tab-${i + 1}`;
        if (usedSlugs.has(slug)) {
          let n = 2;
          while (usedSlugs.has(`${slug}-${n}`)) n++;
          slug = `${slug}-${n}`;
        }
        usedSlugs.add(slug);
        return {
          id: String(t.id),
          slug,
          label,
          filters: t.filters ?? null,
          // A tab may omit pageContent and inherit the top-level one as a fallback.
          pageContent: Array.isArray(t.pageContent) ? t.pageContent : (config?.pageContent ?? []),
        };
      });

    this.tabs = tabs;

    if (!tabs.length) {
      this.activeTabId = null;
      return config;
    }

    // Resolve the active tab from the URL `?tab=` param, matching by SLUG first
    // (clean URLs) then by raw id (backward compatible with old links). Else
    // keep the current selection, else first tab.
    const urlTab = this.route.snapshot.queryParamMap.get('tab');
    const fromUrl = this.findTabByUrlKey(tabs, urlTab);
    const resolvedId =
      fromUrl?.id ||
      (this.activeTabId && tabs.some((t) => t.id === this.activeTabId) && this.activeTabId) ||
      tabs[0].id;
    this.activeTabId = resolvedId;

    const active = tabs.find((t) => t.id === resolvedId) ?? tabs[0];

    // Flattened, single-page-shaped config for the downstream pipeline. Global
    // filters stay on `config.filters`; the tab's local filters are rendered
    // separately by the template (activeTabFilters) and share the same service.
    return { ...config, pageContent: active.pageContent ?? [] };
  }

  private applyDashboardWithLayout(config: any): void {
    // Tabs layer: if the config declares tabs, collapse it into an equivalent
    // single-page config (active tab's pageContent) so the entire pipeline
    // below runs unchanged. No-op for classic single-page dashboards.
    const flattened = this.resolveActiveTabConfig(config);

    const activeLayoutId = this.layoutService.getActiveLayoutId();
    const reshaped = this.layoutService.reshapePageContent(
      flattened?.pageContent,
      activeLayoutId,
    );
    const effective = this.roleVisibilityService.applyToDashboardConfig({ ...flattened, pageContent: reshaped });
    // Include the active tab id in the signature so a tab switch (same layout,
    // different pageContent) is never mistaken for an unchanged emission.
    // Include sourceKey too: two DIFFERENT dashboards (e.g. two dynamic-api
    // dashboards reached via `/dynamic-dashboard/<slug>`) can reshape to an
    // identical pageContent structure — same single widget in the same grid
    // slot — so a content-only signature would match and skip the re-render,
    // leaving the previous dashboard's charts frozen on screen. Keying on
    // sourceKey (which carries the slug) guarantees a dashboard switch always
    // re-renders.
    const signature = JSON.stringify({ source: this.sourceKey, tab: this.activeTabId, content: reshaped ?? null });
    this.setDashboardTitleContext(effective);
    if (signature === this.appliedLayoutSignature) return;
    this.appliedLayoutSignature = signature;

    //  HEIGHT SYSTEM
    this.configurations = this.applyWidgetHeights(effective);
    this.applyNavigationFiltersToGlobalService(this.configurations);

    //  SCREEN FLOATING
    this.applyScreenFloating(this.configurations);

    //  GRID ENGINE (CRITICAL)
    this.applyConfig();

    //  RESPONSIVE SERVICE
    this.responsiveLayoutService.setRowCount(
      this.configurations?.pageContent?.length || 0,
    );

    // ECharts doesn't observe its container — without an explicit resize()
    // signal after a layout/reshape, charts keep stale dimensions and
    // visibly misbehave. RAF lets the new grid styles flush before reflow.
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  /**
   * Push navigation-carried values into GlobalFilterService using dashboard
   * filter metadata so charts receive them as request/query params on first load.
   */
  private applyNavigationFiltersToGlobalService(config: any): void {
    if (this.navFiltersApplied) return;

    const fromNav = this.buildInitialFiltersFromNavigation();
    const dashboardFilters = config?.filters?.content || [];
    console.log('[DVM NAV DEBUG] fromNav:', fromNav);
    console.log('[DVM NAV DEBUG] dashboard filter keys:', (dashboardFilters || []).map((f: any) => f?.key));

    const queryParams = { ...(fromNav.queryParams || {}) } as Record<string, any>;
    const hasNavigationListeners = !!fromNav.filterListener?.length;
    const navigationRequestParams = hasNavigationListeners
      ? { ...(fromNav.requestParams || {}) }
      : {};
    const requestParams = hasNavigationListeners
      ? {}
      : ({ ...(fromNav.requestParams || {}) } as Record<string, any>);

    if (dashboardFilters?.length) {
      for (const filter of dashboardFilters) {
        const key = String(filter?.key || '').trim();
        if (!key) continue;
        const raw = queryParams[key] ?? requestParams[key];
        if (raw === undefined || raw === null || raw === '') continue;

        const normalized = Array.isArray(raw)
          ? raw
          : (filter?.isMultiSelect ? [raw] : raw);

        if (filter?.isQueryParam) {
          queryParams[key] = normalized;
        } else {
          requestParams[key] = normalized;
        }
      }
    }

    const nextFilters = {
      queryParams,
      requestParams,
      widgetId: null,
      ...(hasNavigationListeners ? { navigationRequestParams } : {}),
      ...(hasNavigationListeners ? { navigationFilterListener: fromNav.filterListener } : {}),
      ...(fromNav.sourceWidgetId ? { navigationSourceWidgetId: fromNav.sourceWidgetId } : {}),
    };

    this.globalFilterService.updateAllFilters(nextFilters);
    console.log('[DVM NAV DEBUG] applied global filters:', {
      queryParams,
      requestParams,
      navigationRequestParams,
      filterListener: fromNav.filterListener,
    });
    this.navFiltersApplied = true;
  }

  /** Re-apply the active layout against the cached original config.
   *  No-op until the dashboard config has loaded. */
  private maybeReapplyLayout(): void {
    if (!this.originalDashboardConfig) return;
    this.applyDashboardWithLayout(this.originalDashboardConfig);
  }

  /**
   * Switch the active dashboard tab. Persists nothing on the server — the tab
   * selection lives in the URL (`?tab=<id>`) so it is shareable and works with
   * browser back/forward. Re-runs the grid pipeline for the newly-active tab
   * and nudges ECharts to resize once the new layout has flushed.
   */
  switchTab(tabId: string): void {
    if (!tabId || tabId === this.activeTabId) return;
    const target = this.tabs.find((t) => t.id === tabId);
    if (!target) return;

    this.activeTabId = tabId;

    // Reflect the selection in the URL using the tab's SLUG (clean, shareable)
    // rather than its internal id. Merges with existing filter params. The
    // queryParamMap subscription re-invokes the pipeline, but we also reapply
    // directly so the switch feels instant before navigation settles.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: target.slug },
      queryParamsHandling: 'merge',
    });

    this.maybeReapplyLayout();
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  private applyScreenFloating(config: any): void {
    // Default to true (scrolling enabled) if key is not present
    this.screenFloating = config?.screenFloating !== false;
    this.responsiveLayoutService.setScreenFloating(this.screenFloating);
    if (this.screenFloating) {
      this.responsiveLayoutService.setCompactLayoutFitOverride(null);
      this.compactScrollLock = false;
      this.compactScrollLockSnapshot = null;
    }
  }

  private applyWidgetHeights(config: any): any {
    if (!config || !Array.isArray(config.pageContent)) {
      return config;
    }

    const updatedPageContent = config.pageContent.map((row: any) => ({
      ...row,
      columns: this.normalizeColumns(row?.columns || []),
    }));

    return {
      ...config,
      pageContent: updatedPageContent,
    };
  }

  private applyWidgetHeightsInPlace(config: any): void {
    if (!config || !Array.isArray(config.pageContent)) {
      return;
    }

    for (const row of config.pageContent) {
      this.applyColumnHeightsInPlace(row?.columns || []);
    }
  }

  private applyColumnHeightsInPlace(columns: any[]): void {
    for (const column of columns) {
      if (column?.type) {
        column.height = this.resolveWidgetHeight(column);
      }

      if (Array.isArray(column?.columns)) {
        this.applyColumnHeightsInPlace(column.columns);
      }

      if (Array.isArray(column?.rows)) {
        for (const nestedRow of column.rows) {
          if (nestedRow?.type) {
            nestedRow.height = this.resolveWidgetHeight(nestedRow);
          }

          if (Array.isArray(nestedRow?.columns)) {
            this.applyColumnHeightsInPlace(nestedRow.columns);
          }

          if (Array.isArray(nestedRow?.rows)) {
            this.applyColumnHeightsInPlace(nestedRow.rows);
          }
        }
      }
    }
  }

  private normalizeColumns(columns: any[]): any[] {
    return columns.map((column: any) => {
      const updatedColumn = { ...column };

      if (updatedColumn?.type) {
        updatedColumn.height = this.resolveWidgetHeight(updatedColumn);
      }

      if (Array.isArray(updatedColumn?.columns)) {
        updatedColumn.columns = this.normalizeColumns(updatedColumn.columns);
      }

      if (Array.isArray(updatedColumn?.rows)) {
        updatedColumn.rows = updatedColumn.rows.map((nestedRow: any) => ({
          ...nestedRow,
          columns: this.normalizeColumns(nestedRow?.columns || []),
          ...(nestedRow?.type
            ? { height: this.resolveWidgetHeight(nestedRow) }
            : {}),
        }));
      }

      return updatedColumn;
    });
  }

  private resolveWidgetHeight(widget: any): string {
    return this.responsiveLayoutService.resolveWidgetHeight(widget);
  }



  @HostListener('window:resize')
  onViewportChange(): void {
    this.containerWidth = this.gridContainerRef?.nativeElement?.clientWidth || window.innerWidth;
    this.currentBp = this.resolveBp(window.innerWidth);
    if (!this.layouts[this.currentBp]?.length) this.layouts[this.currentBp] = this.buildDefaultLayout(this.currentBp);
    this.updateRowsForResponsiveService();
    this.releaseCompactScrollLockIfViewportImproved();
    this.queueCompactFitMeasurement();
  }

  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.interaction || event.pointerId !== this.interaction.pointerId) return;
    event.preventDefault();
    this.queued = { x: event.clientX, y: event.clientY };
    if (this.raf) return;
    this.raf = true;
    requestAnimationFrame(() => {
      this.raf = false;
      if (!this.interaction || !this.queued) return;
      this.applyInteraction(this.queued.x, this.queued.y);
      this.queued = null;
    });
  }

  @HostListener('window:pointerup', ['$event'])
  @HostListener('window:pointercancel', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.interaction || event.pointerId !== this.interaction.pointerId) return;
    this.finishInteraction(false);
  }

  @HostListener('window:blur')
  onBlur(): void {
    if (this.interaction) this.finishInteraction(false);
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    void this.exitFocusMode();
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    const prevFocusedId = this.focusedWidgetId;
    const active = document.fullscreenElement as HTMLElement | null;
    const nextId = active?.getAttribute('data-widget-id') || null;
    if (active) {
      this.syncChartTooltipContainer(active, true);
      this.startFocusTooltipSync(active);
    } else {
      this.stopFocusTooltipSync();
    }
    if (!nextId && prevFocusedId) {
      const prevEl = this.getWidgetElement(prevFocusedId);
      if (prevEl) this.syncChartTooltipContainer(prevEl, false);
    }
    this.setFocusedWidget(nextId);
  }

  widgetStyle(item: GridItem): Record<string, string> {
    return { 'grid-column': `${item.x + 1} / span ${item.w}`, 'grid-row': `${item.y + 1} / span ${item.h}` };
  }

  label(widget: any): string {
    return String(widget?.title || widget?.widgetId || 'widget');
  }

  focusToggleIcon(id: string): string {
    return this.isFocused(id) ? 'close_fullscreen' : 'open_in_full';
  }

  focusToggleLabel(id: string, widget: any): string {
    const action = this.isFocused(id) ? 'Close focus for ' : 'Open focus for ';
    return `${action}${this.label(widget)}`;
  }

  isFocused(id: string): boolean {
    return this.focusedWidgetId === id;
  }

  canShowFocusToggle(widget: any): boolean {
    if (!this.focusMode.enabled) return false;
    const type = this.normalizeType(widget?.type);
    if (!type) return false;
    if (this.focusMode.excludeTypes.has(type)) return false;
    if (this.focusMode.includeTypes.size) return this.focusMode.includeTypes.has(type);
    return !NON_FOCUS_WIDGET_TYPES.has(type);
  }

  onFocusTogglePointerDown(event: PointerEvent): void {
    event.stopPropagation();
  }

  toggleFocus(id: string): void {
    if (this.focusedWidgetId === id) {
      void this.exitFocusMode();
      return;
    }
    void this.enterFocusMode(id);
  }

  canDrag(item: GridItem): boolean { return this.editMode && !this.hasFocusWidget && this.grid.draggable && !item.locked; }
  canResize(item: GridItem): boolean { return this.editMode && !this.hasFocusWidget && this.grid.resizable && !item.locked; }
  isDragging(id: string): boolean { return this.activeDragId === id; }
  isResizing(id: string): boolean { return this.activeResizeId === id; }

  enterEditMode(): void {
    if (!this.layoutEditEnabled) return;
    if (this.editMode) return;

    this.editSessionLayouts = this.cloneLayouts(this.layouts);
    this.editMode = true;
  }

  cancelEditMode(): void {
    if (!this.editMode) return;

    if (this.interaction) {
      this.finishInteraction(false);
    }

    const resetLayouts = this.cloneLayouts(this.editSessionLayouts);
    if (resetLayouts) {
      this.layouts = resetLayouts;
      this.syncDashboardAfterLayoutChange();
    }

    this.exitEditMode();
  }

  saveMyLayout(): void {
    this.saveLayout('USER');
  }

  saveDefaultLayout(): void {
    if (!this.canSaveDefaultLayout) return;
    this.saveLayout('GLOBAL');
  }

  startDrag(event: PointerEvent, item: GridItem): void { this.startInteraction(event, item, 'drag'); }
  startResize(event: PointerEvent, item: GridItem, corner: 'nw' | 'ne' | 'sw' | 'se'): void {
    this.startInteraction(event, item, 'resize', corner);
  }
  onWidgetPointerDown(event: PointerEvent, item: GridItem): void {
    if (!this.editMode || !this.canDrag(item)) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('.resize-handle') || target.closest('.focus-mode-toggle')) return;
    const host = event.currentTarget as HTMLElement | null;
    const hasHeader = !!host?.querySelector('.card-header');
    // If widget has a card header, drag starts from header only.
    if (hasHeader && !target.closest('.card-header')) return;
    this.startDrag(event, item);
  }

  private applyConfig(): void {
    this.responsiveLayoutService.setScreenFloating(this.configurations?.screenFloating !== false);
    this.widgets = this.extractWidgets(this.configurations?.pageContent ?? []);
    this.widgetMap = new Map(this.widgets.map((w) => [w.id, w]));
    this.resolveFocusModeConfig();
    if (this.focusedWidgetId) {
      const focusedRec = this.widgetMap.get(this.focusedWidgetId);
      if (!focusedRec || !this.canShowFocusToggle(focusedRec.widget)) void this.exitFocusMode();
    }
    this.resolveGridConfig();
    this.resolveLayoutEditConfig();
    this.layouts = {};
    const persisted = this.readPersisted();
    for (const bp of this.orderedBps) {
      const base = this.buildDefaultLayout(bp);
      const raw = this.parseRawLayout(this.grid.rawLayouts[bp] ?? [], bp);
      const mergedA = raw.length ? this.merge(base, raw, bp) : base;
      const mergedB = persisted?.[bp]?.length ? this.merge(mergedA, persisted[bp], bp) : mergedA;
      this.layouts[bp] = this.pack(mergedB, null, this.grid.columns[bp] ?? 12);
    }
    this.onViewportChange();
    this.queueCompactFitMeasurement();
  }

  private resolveGridConfig(): void {
    const c = this.configurations?.layoutConfig ?? this.configurations?.gridLayout ?? {};
    const defaultWidgetHeight =
      String(
        this.configurations?.defaultWidgetHeight ??
        this.configurations?.defaultHeight ??
        this.configurations?.defaultheight ??
        c?.defaultWidgetHeight ??
        c?.defaultheight ??
        ''
      ).trim();
    if (defaultWidgetHeight) {
      this.responsiveLayoutService.setDefaultWidgetHeight(defaultWidgetHeight);
    }
    this.grid.breakpoints = this.mapNumbers(c?.breakpoints, BPS);
    this.grid.columns = this.mapNumbers(c?.columns ?? c?.cols, COLS);
    this.grid.rowHeight = this.num(c?.rowHeight, 72);
    this.grid.gap = this.num(Array.isArray(c?.margin) ? c.margin[0] : (c?.gap ?? c?.margin), 12);
    this.grid.draggable = c?.draggable !== false;
    this.grid.resizable = c?.resizable !== false;
    this.grid.persist = c?.persist !== false;
    this.grid.persistKey = String(c?.persistKey ?? '').trim();
    this.grid.defaultW = this.num(c?.defaultWidth, 4);
    this.grid.defaultH = this.num(c?.defaultHeight, 4);
    this.grid.defaultMinW = this.num(c?.defaultMinW, 2);
    this.grid.defaultMinH = this.num(c?.defaultMinH, 2);
    this.grid.defaultMaxH = this.num(c?.defaultMaxH, 16);
    this.grid.rawLayouts = typeof c?.layouts === 'object' && c?.layouts ? c.layouts : {};
  }

  private resolveLayoutEditConfig(): void {
    const c = this.configurations?.layoutConfig ?? this.configurations?.gridLayout ?? {};
    const enabled = this.firstEnabledFlag(
      this.configurations?.enableEdit,
      this.configurations?.editable,
      this.configurations?.editMode,
      this.configurations?.layoutEdit,
      this.configurations?.layoutEditMode,
      c?.enableEdit,
      c?.editable,
      c?.edit,
      c?.editMode,
      c?.layoutEdit
    );

    this.layoutEditEnabled = enabled === true;

    if (!this.layoutEditEnabled && this.editMode) {
      if (this.interaction) this.finishInteraction(false);
      this.editMode = false;
      this.activeDragId = null;
      this.activeResizeId = null;
    }
  }

  private resolveFocusModeConfig(): void {
    const dashboardConfig = this.configurations?.focusMode
      ?? this.configurations?.enableFocusMode
      ?? this.configurations?.focus;
    const gridConfig = this.configurations?.layoutConfig?.focusMode
      ?? this.configurations?.layoutConfig?.enableFocusMode
      ?? this.configurations?.gridLayout?.focusMode
      ?? this.configurations?.gridLayout?.enableFocusMode;
    const raw = dashboardConfig ?? gridConfig;

    if (typeof raw === 'boolean') {
      this.focusMode = { enabled: raw, includeTypes: new Set<string>(), excludeTypes: new Set<string>() };
      if (!raw) void this.exitFocusMode();
      return;
    }

    if (!raw || typeof raw !== 'object') {
      this.focusMode = { enabled: false, includeTypes: new Set<string>(), excludeTypes: new Set<string>() };
      void this.exitFocusMode();
      return;
    }

    const cfg = raw as Record<string, unknown>;
    this.focusMode = {
      enabled: this.toEnabledFlag(cfg) === true,
      includeTypes: new Set<string>(this.mapStrings(cfg['includeTypes'] ?? cfg['types'] ?? cfg['chartTypes'])),
      excludeTypes: new Set<string>(this.mapStrings(cfg['excludeTypes'] ?? cfg['disabledTypes'])),
    };

    if (!this.focusMode.enabled) void this.exitFocusMode();
  }

  private async enterFocusMode(id: string): Promise<void> {
    const rec = this.widgetMap.get(id);
    if (!rec || !this.canShowFocusToggle(rec.widget)) return;

    const target = this.getWidgetElement(id);
    if (!target?.requestFullscreen) return;

    if (this.interaction) this.finishInteraction(false);
    try {
      if (document.fullscreenElement && document.fullscreenElement !== target) {
        const active = document.fullscreenElement as HTMLElement | null;
        if (active) this.syncChartTooltipContainer(active, false);
        this.stopFocusTooltipSync();
        await document.exitFullscreen();
      }
      await target.requestFullscreen();
      this.syncChartTooltipContainer(target, true);
      this.startFocusTooltipSync(target);
      this.setFocusedWidget(id);
    } catch (error) {
      console.error('Unable to enter focus mode fullscreen:', error);
      this.syncChartTooltipContainer(target, false);
      this.stopFocusTooltipSync();
      this.setFocusedWidget(null);
    }
  }

  private async exitFocusMode(): Promise<void> {
    this.stopFocusTooltipSync();
    const active = document.fullscreenElement as HTMLElement | null;
    if (active) this.syncChartTooltipContainer(active, false);
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch (error) {
      console.error('Unable to exit focus mode fullscreen:', error);
    } finally {
      this.setFocusedWidget(null);
    }
  }

  private setFocusedWidget(id: string | null): void {
    let nextId = id;
    if (nextId) {
      const rec = this.widgetMap.get(nextId);
      if (!rec || !this.canShowFocusToggle(rec.widget)) nextId = null;
    }
    if (this.focusedWidgetId === nextId) return;

    if (this.interaction) this.finishInteraction(false);
    this.focusedWidgetId = nextId;
    this.syncFocusBodyClass();
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  private getWidgetElement(id: string): HTMLElement | null {
    return this.gridContainerRef?.nativeElement
      ?.querySelector(`.grid-widget[data-widget-id="${id}"]`) as HTMLElement | null;
  }

  private syncChartTooltipContainer(widgetEl: HTMLElement, fullscreen: boolean, triggerReflow = true): void {
    const chartDoms = this.collectChartDoms(widgetEl);
    chartDoms.forEach((chartHost) => {
      const chart = echarts.getInstanceByDom(chartHost);
      if (!chart) return;

      const option = chart.getOption() as any;
      const tooltipList = Array.isArray(option?.tooltip)
        ? option.tooltip
        : (option?.tooltip ? [option.tooltip] : []);
      if (!tooltipList.length) return;

      if (fullscreen) {
        if (!this.tooltipAppendSnapshot.has(chartHost)) {
          this.tooltipAppendSnapshot.set(
            chartHost,
            tooltipList.map((t: any) => ({
              appendToBody: (t && typeof t === 'object' && typeof t.appendToBody === 'boolean') ? t.appendToBody : undefined,
              appendTo: (t && typeof t === 'object') ? t.appendTo : undefined,
            }))
          );
        }

        const patch = tooltipList.map((t: any) => ({
          ...(t && typeof t === 'object' ? t : {}),
          appendToBody: false,
          appendTo: widgetEl,
        }));
        chart.setOption({ tooltip: patch.length === 1 ? patch[0] : patch }, { lazyUpdate: true });
        return;
      }

      const snapshot = this.tooltipAppendSnapshot.get(chartHost);
      if (!snapshot?.length) return;
      const patch = tooltipList.map((t: any, idx: number) => {
        const snap = snapshot[idx];
        return {
          ...(t && typeof t === 'object' ? t : {}),
          appendToBody: snap?.appendToBody === true,
          appendTo: snap && 'appendTo' in snap ? snap.appendTo : null,
        };
      });
      chart.setOption({ tooltip: patch.length === 1 ? patch[0] : patch }, { lazyUpdate: true });
      this.tooltipAppendSnapshot.delete(chartHost);
    });

    if (triggerReflow) {
      requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
    }
  }

  private collectChartDoms(widgetEl: HTMLElement): HTMLElement[] {
    const found = new Set<HTMLElement>();
    const els = widgetEl.querySelectorAll('*');
    els.forEach((n) => {
      const chart = echarts.getInstanceByDom(n as HTMLElement);
      if (chart) {
        const dom = chart.getDom() as HTMLElement;
        if (dom) found.add(dom);
      }
    });
    return [...found];
  }

  private startFocusTooltipSync(widgetEl: HTMLElement): void {
    this.stopFocusTooltipSync();
    this.focusTooltipSyncTimer = setInterval(() => {
      const active = document.fullscreenElement as HTMLElement | null;
      if (!active || active !== widgetEl) {
        this.stopFocusTooltipSync();
        return;
      }
      this.syncChartTooltipContainer(widgetEl, true, false);
    }, 300);
  }

  private stopFocusTooltipSync(): void {
    if (!this.focusTooltipSyncTimer) return;
    clearInterval(this.focusTooltipSyncTimer);
    this.focusTooltipSyncTimer = undefined;
  }

  private syncFocusBodyClass(): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('dashboard-focus-active', !!this.focusedWidgetId);
  }

  private startInteraction(
    event: PointerEvent,
    item: GridItem,
    mode: 'drag' | 'resize',
    corner?: 'nw' | 'ne' | 'sw' | 'se'
  ): void {
    if (event.button !== 0 || (mode === 'drag' && !this.canDrag(item)) || (mode === 'resize' && !this.canResize(item))) return;
    event.preventDefault(); event.stopPropagation();
    this.interaction = {
      mode, id: item.id, corner, pointerId: event.pointerId, startClientX: event.clientX, startClientY: event.clientY,
      startX: item.x, startY: item.y, startW: item.w, startH: item.h,
    };
    this.activeDragId = mode === 'drag' ? item.id : null;
    this.activeResizeId = mode === 'resize' ? item.id : null;
    const target = event.currentTarget as HTMLElement | null;
    this.interactionTarget = target ?? undefined;
    target?.setPointerCapture?.(event.pointerId);
  }

  private applyInteraction(clientX: number, clientY: number): void {
    if (!this.interaction) return;
    const item = (this.layouts[this.currentBp] ?? []).find((x) => x.id === this.interaction?.id);
    if (!item) return;
    const dx = Math.round((clientX - this.interaction.startClientX) / (this.cellW() + this.grid.gap));
    const dy = Math.round((clientY - this.interaction.startClientY) / (this.grid.rowHeight + this.grid.gap));
    if (this.interaction.mode === 'drag') {
      this.updateItem(item.id, { x: this.clamp(this.interaction.startX + dx, 0, this.cols - item.w), y: Math.max(0, this.interaction.startY + dy) });
    } else {
      const i = this.interaction;
      const corner = i.corner ?? 'se';
      let x = i.startX;
      let y = i.startY;
      let w = i.startW;
      let h = i.startH;

      if (corner === 'se') {
        const maxW = Math.min(item.maxW, this.cols - i.startX);
        w = this.clamp(i.startW + dx, item.minW, maxW);
        h = this.clamp(i.startH + dy, item.minH, item.maxH);
      } else if (corner === 'sw') {
        const maxW = Math.min(item.maxW, i.startX + i.startW);
        w = this.clamp(i.startW - dx, item.minW, maxW);
        x = i.startX + (i.startW - w);
        h = this.clamp(i.startH + dy, item.minH, item.maxH);
      } else if (corner === 'ne') {
        const maxW = Math.min(item.maxW, this.cols - i.startX);
        const maxH = Math.min(item.maxH, i.startY + i.startH);
        w = this.clamp(i.startW + dx, item.minW, maxW);
        h = this.clamp(i.startH - dy, item.minH, maxH);
        y = i.startY + (i.startH - h);
      } else {
        const maxW = Math.min(item.maxW, i.startX + i.startW);
        const maxH = Math.min(item.maxH, i.startY + i.startH);
        w = this.clamp(i.startW - dx, item.minW, maxW);
        h = this.clamp(i.startH - dy, item.minH, maxH);
        x = i.startX + (i.startW - w);
        y = i.startY + (i.startH - h);
      }

      this.updateItem(item.id, { x, y, w, h });
    }
  }

  private finishInteraction(persist: boolean): void {
    if (!this.interaction) return;
    try { this.interactionTarget?.releasePointerCapture?.(this.interaction.pointerId); } catch { }
    this.interaction = undefined; this.interactionTarget = undefined; this.activeDragId = null; this.activeResizeId = null; this.queued = null; this.raf = false;
    if (persist) this.writePersisted();
  }

  private updateItem(id: string, changes: Partial<GridItem>): void {
    const cur = this.layouts[this.currentBp] ?? [];
    const idx = cur.findIndex((x) => x.id === id);
    if (idx < 0) return;
    const next = cur.map((x) => ({ ...x }));
    next[idx] = this.sanitize({ ...next[idx], ...changes }, this.cols);
    this.layouts[this.currentBp] = this.pack(next, id, this.cols);
    this.updateRowsForResponsiveService();
  }

  private buildDefaultLayout(bp: string): GridItem[] {
    const cols = this.grid.columns[bp] ?? 12;
    const raw = this.widgets.map((rec) => {
      // DVM layouts persist their exact 12-column footprint as layout.colSpan.
      // Older published records may not have the equivalent `col-N` class, so
      // use the structured span instead of silently expanding them to 12 cols.
      const savedColSpan = Number(rec.widget?.layout?.colSpan);
      const sourceSpan = rec.widget?.class || (Number.isFinite(savedColSpan) && savedColSpan > 0 ? `col-${savedColSpan}` : '');
      const w = this.clamp(this.spanToCols(this.span(sourceSpan, bp), cols), 1, cols);
      const minW = this.clamp(this.num(rec.widget?.minW, this.grid.defaultMinW), 1, cols);
      const minH = this.clamp(this.num(rec.widget?.minH, this.grid.defaultMinH), 1, this.grid.defaultMaxH);
      const h = this.clamp(
        this.heightToRows(this.resolveWidgetHeightForBreakpoint(rec.widget, bp)),
        minH,
        this.grid.defaultMaxH
      );
      return { id: rec.id, x: 0, y: 0, w: this.clamp(w, minW, cols), h, minW, maxW: this.clamp(this.num(rec.widget?.maxW, cols), minW, cols), minH, maxH: this.clamp(this.num(rec.widget?.maxH, this.grid.defaultMaxH), minH, this.grid.defaultMaxH), locked: !!rec.widget?.locked };
    });
    let cursorX = 0, cursorY = 0, rowH = 0;
    for (const item of raw) {
      if (cursorX + item.w > cols) { cursorX = 0; cursorY += rowH; rowH = 0; }
      item.x = cursorX; item.y = cursorY;
      cursorX += item.w; rowH = Math.max(rowH, item.h);
    }
    return this.pack(raw, null, cols);
  }

  private parseRawLayout(raw: Array<Record<string, unknown>>, bp: string): GridItem[] {
    const cols = this.grid.columns[bp] ?? 12;
    return raw.map((r) => {
      const id = String(r['id'] ?? r['i'] ?? '');
      const wrec = this.widgetMap.get(id)?.widget;
      const minW = this.clamp(this.num(r['minW'] ?? wrec?.minW, this.grid.defaultMinW), 1, cols);
      const minH = this.clamp(this.num(r['minH'] ?? wrec?.minH, this.grid.defaultMinH), 1, this.grid.defaultMaxH);
      const maxW = this.clamp(this.num(r['maxW'] ?? wrec?.maxW, cols), minW, cols);
      const maxH = this.clamp(this.num(r['maxH'] ?? wrec?.maxH, this.grid.defaultMaxH), minH, this.grid.defaultMaxH);
      return this.sanitize({ id, x: this.num(r['x'], 0), y: this.num(r['y'], 0), w: this.num(r['w'], this.grid.defaultW), h: this.num(r['h'], this.grid.defaultH), minW, maxW, minH, maxH, locked: !!(r['locked'] ?? wrec?.locked) }, cols);
    }).filter((x) => !!x.id && this.widgetMap.has(x.id));
  }

  private merge(
    base: GridItem[],
    overlay: GridItem[],
    bp: string,
    options?: { preserveBaseHeight?: boolean }
  ): GridItem[] {
    const cols = this.grid.columns[bp] ?? 12;
    const map = new Map(overlay.map((x) => [x.id, x]));
    return base.map((b) => {
      const merged = map.has(b.id) ? { ...b, ...map.get(b.id)! } : { ...b };
      if (options?.preserveBaseHeight) {
        merged.h = b.h;
      }
      return this.sanitize(merged, cols);
    });
  }

  private pack(items: GridItem[], activeId: string | null, cols: number): GridItem[] {
    const order = [...items].sort((a, b) => {
      if (activeId) { if (a.id === activeId && b.id !== activeId) return -1; if (b.id === activeId && a.id !== activeId) return 1; }
      return a.y - b.y || a.x - b.x;
    });
    const placed: GridItem[] = [];
    for (const it of order) { const c = this.sanitize(it, cols); while (this.collides(c, placed)) c.y += 1; placed.push(c); }
    const compacted: GridItem[] = [];
    for (const it of placed.sort((a, b) => a.y - b.y || a.x - b.x)) {
      const c = this.sanitize({ ...it }, cols);
      if (it.id !== activeId) while (c.y > 0 && !this.collides({ ...c, y: c.y - 1 }, compacted)) c.y -= 1;
      while (this.collides(c, compacted)) c.y += 1;
      compacted.push(c);
    }
    const map = new Map(compacted.map((x) => [x.id, x]));
    return this.widgets.map((w) => map.get(w.id)).filter((x): x is GridItem => !!x);
  }

  private collides(a: GridItem, list: GridItem[]): boolean {
    return list.some((b) => a.id !== b.id && a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y);
  }

  private sanitize(it: GridItem, cols: number): GridItem {
    const minW = this.clamp(it.minW, 1, cols), maxW = this.clamp(it.maxW, minW, cols);
    const minH = this.clamp(it.minH, 1, this.grid.defaultMaxH), maxH = this.clamp(it.maxH, minH, this.grid.defaultMaxH);
    const w = this.clamp(it.w, minW, maxW), h = this.clamp(it.h, minH, maxH);
    return { ...it, x: this.clamp(it.x, 0, cols - w), y: Math.max(0, Math.floor(it.y)), w, h, minW, maxW, minH, maxH, locked: !!it.locked };
  }

  private extractWidgets(content: any[]): WidgetRec[] {
    const out: WidgetRec[] = []; const count = new Map<string, number>();
    const visit = (n: any): void => {
      if (!n || typeof n !== 'object') return;
      if (n.type) {
        const base = String(n.widgetId ?? n.id ?? `widget-${out.length + 1}`).replace(/[^a-zA-Z0-9-_]/g, '-');
        const idx = (count.get(base) ?? 0) + 1; count.set(base, idx);
        if (n.ssWidgetKind) {
          n.onWidgetValueChange = (value: Record<string, unknown>) => this.onPublishedWidgetValueChange(n, value);
        } else if ((n.type === 'slider' || n.type === 'input-box') && (n.recomputeUrl || n.payload)) {
          // DVM-layout input control: re-post its payload on change and live-
          // update the target charts from the response's `results[]`.
          n.onWidgetValueChange = (value: unknown) => this.onDvmInputRecompute(n, value);
        }
        out.push({ id: idx === 1 ? base : `${base}__${idx}`, widget: n });
        // A typed node is a widget config. Its own `columns`/`rows` can be
        // component-specific config, such as super-table columns, not dashboard
        // layout children. Stop here to avoid rendering those configs as blank
        // dashboard widgets.
        return;
      }
      if (Array.isArray(n.columns)) n.columns.forEach((x: any) => visit(x));
      if (Array.isArray(n.rows)) n.rows.forEach((x: any) => visit(x));
      if (Array.isArray(n.stats)) n.stats.forEach((x: any) => visit(x));
    };
    content.forEach((x) => visit(x));
    return out;
  }

  private readPersisted(): Record<string, GridItem[]> | null {
    if (!this.grid.persist) return null;
    // When a custom layout is active, the layout's row pattern is the source
    // of truth. Drag positions saved against a previous layout (or no layout)
    // would override the new column widths and leave cycled rows misaligned.
    if (this.layoutService.getActiveLayoutId()) return null;
    return (
      this.cloneLayouts(this.persistedLayouts) ??
      this.readPersistedFromLocalStorage(this.grid.persist)
    );
  }

  private writePersisted(scope: LayoutPersistenceScope = 'USER'): void {
    if (!this.grid.persist) return;
    const snapshot = this.buildPersistedLayoutSnapshot();
    this.persistedLayouts = this.cloneLayouts(snapshot.layouts);
    if (scope === 'USER') {
      this.writePersistedToLocalStorage(snapshot);
    }
    this.writePersistedToApi(snapshot, scope);
  }

  private persistKey(): string {
    const base = this.grid.persistKey
      ? `dashboard-grid:${this.grid.persistKey}`
      : `dashboard-grid:${this.sourceKey || (this.route.snapshot.routeConfig?.path ?? this.route.snapshot.url.map((s) => s.path).join('/')) || 'dashboard'}`;
    // Each tab is its own canvas: scope the saved layout by tab id so drag/
    // resize positions don't bleed across tabs. Single-page dashboards keep the
    // original (un-suffixed) key for backward compatibility.
    return this.activeTabId ? `${base}::tab=${this.activeTabId}` : base;
  }

  private updateRowsForResponsiveService(): void {
    const max = (this.layouts[this.currentBp] ?? []).reduce((m, i) => Math.max(m, i.y + i.h), 0);
    this.responsiveLayoutService.setRowCount(Math.max(1, Math.ceil(max / Math.max(1, this.grid.defaultH))));
  }

  private queueCompactFitMeasurement(): void {
    if (this.screenFloating) {
      this.responsiveLayoutService.setCompactLayoutFitOverride(null);
      return;
    }
    if (this.compactFitRafId !== null) {
      cancelAnimationFrame(this.compactFitRafId);
    }
    this.compactFitRafId = requestAnimationFrame(() => {
      this.compactFitRafId = requestAnimationFrame(() => {
        this.compactFitRafId = null;
        this.measureCompactFit();
      });
    });
  }

  private measureCompactFit(): void {
    if (this.screenFloating) {
      this.responsiveLayoutService.setCompactLayoutFitOverride(null);
      return;
    }

    const contentEl = this.dashboardContentRef?.nativeElement;
    const hostEl = this.hostRef.nativeElement;
    if (!contentEl || !hostEl) return;

    const viewportHost =
      hostEl.closest('mat-sidenav-content, .mat-sidenav-content') as HTMLElement | null;
    const viewportBottom = viewportHost?.getBoundingClientRect().bottom ?? window.innerHeight;
    const contentTop = contentEl.getBoundingClientRect().top;
    const availableHeight = Math.max(0, Math.floor(viewportBottom - contentTop));
    const browserZoomScale = window.visualViewport?.scale ?? 1;
    const requiredHeight = this.responsiveLayoutService.getEstimatedCompactRequiredHeight();
    const currentlyCompact = !this.effectiveScreenFloating;
    const currentSnapshot = this.getCompactViewportSnapshot(requiredHeight);
    const heightMargin = currentlyCompact
      ? DashboardComponent.COMPACT_EXIT_MARGIN_PX
      : DashboardComponent.COMPACT_ENTER_MARGIN_PX;

    let fits =
      browserZoomScale <= DashboardComponent.COMPACT_BASELINE_BROWSER_ZOOM_MAX &&
      availableHeight >= (requiredHeight + heightMargin);

    if (!currentlyCompact && this.compactScrollLock) {
      const locked = this.compactScrollLockSnapshot;
      const viewportImproved = !!locked && (
        currentSnapshot.width >= locked.width + 48 ||
        currentSnapshot.height >= locked.height + 48 ||
        currentSnapshot.scale <= locked.scale - 0.05
      );
      const heightDemandImproved = !!locked &&
        currentSnapshot.requiredHeight <= Math.max(0, locked.requiredHeight - 24);

      if (!viewportImproved && !heightDemandImproved) {
        fits = false;
      }
    }

    if (currentlyCompact) {
      const gridEl = this.gridContainerRef?.nativeElement;
      const contentOverflow = contentEl.scrollHeight > (contentEl.clientHeight + 2);
      const gridOverflow = !!gridEl && gridEl.scrollHeight > (contentEl.clientHeight + 2);
      const minChartHeight = this.findSmallestChartHostHeight(gridEl);
      const chartTooShort =
        minChartHeight > 0 &&
        minChartHeight < this.responsiveLayoutService.getCompactMinimumChartHeight();

      if (contentOverflow || gridOverflow || chartTooShort) {
        fits = false;
      }
    }

    if (!fits) {
      this.compactScrollLock = true;
      this.compactScrollLockSnapshot = currentSnapshot;
    } else if (currentlyCompact || this.compactScrollLock) {
      this.compactScrollLock = false;
      this.compactScrollLockSnapshot = null;
    }

    this.responsiveLayoutService.setCompactLayoutFitOverride(fits);
  }

  private findSmallestChartHostHeight(gridEl?: HTMLElement): number {
    if (!gridEl) return 0;

    let minHeight = Number.POSITIVE_INFINITY;
    const chartHosts = gridEl.querySelectorAll('.card-body > div[id]');
    chartHosts.forEach((el) => {
      const height = (el as HTMLElement).clientHeight;
      if (height > 0 && height < minHeight) {
        minHeight = height;
      }
    });

    return Number.isFinite(minHeight) ? minHeight : 0;
  }

  private getCompactViewportSnapshot(requiredHeight = this.responsiveLayoutService.getEstimatedCompactRequiredHeight()): CompactViewportSnapshot {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      scale: window.visualViewport?.scale ?? 1,
      requiredHeight,
    };
  }

  private releaseCompactScrollLockIfViewportImproved(): void {
    if (this.screenFloating || !this.compactScrollLock || !this.compactScrollLockSnapshot) return;

    const current = this.getCompactViewportSnapshot();
    const locked = this.compactScrollLockSnapshot;
    const widthImproved = current.width >= locked.width + 48;
    const heightImproved = current.height >= locked.height + 48;
    const scaleImproved = current.scale <= locked.scale - 0.05;
    const requiredHeightImproved = current.requiredHeight <= Math.max(0, locked.requiredHeight - 24);

    if (widthImproved || heightImproved || scaleImproved || requiredHeightImproved) {
      this.compactScrollLock = false;
      this.compactScrollLockSnapshot = null;
    }
  }

  private resolveBp(width: number): string { return this.orderedBps.find((bp) => width >= (this.grid.breakpoints[bp] ?? 0)) ?? 'lg'; }
  private cellW(): number { return (Math.max(0, this.containerWidth - (this.cols - 1) * this.grid.gap)) / Math.max(1, this.cols); }
  private spanToCols(span: number, cols: number): number { return Math.round((span / 12) * cols) || cols; }
  private getGridItemChromeHeightPx(): number {
    const fontScale = Math.max(1, this.responsiveLayoutService.getFontScale());
    return Math.round(72 * fontScale);
  }
  private resolveWidgetHeightForBreakpoint(widget: any, bp: string): unknown {
    if (!widget || typeof widget !== 'object') {
      return widget;
    }

    if ((bp === 'xxs' || bp === 'xs' || bp === 'sm') && widget.heightSm != null && widget.heightSm !== '') {
      return widget.heightSm;
    }

    if (bp === 'md' && widget.heightMd != null && widget.heightMd !== '') {
      return widget.heightMd;
    }

    if ((bp === 'lg' || bp === 'xl') && widget.heightLg != null && widget.heightLg !== '') {
      return widget.heightLg;
    }

    if (widget.height != null && widget.height !== '') {
      return widget.height;
    }

    return widget?.chartConfig?.height ?? this.responsiveLayoutService.getDefaultWidgetHeight();
  }
  private heightToRows(height: unknown): number {
    const px = this.responsiveLayoutService.resolveHeightToPixels(height) ?? 0;
    const totalPx = px > 0 ? px + this.getGridItemChromeHeightPx() : 0;
    return px > 0
      ? Math.ceil((totalPx + this.grid.gap) / (this.grid.rowHeight + this.grid.gap))
      : this.grid.defaultH;
  }
  private span(cls: unknown, bp: string): number {
    if (typeof cls !== 'string') return 12;
    const chains: Record<string, string[]> = { xl: ['xl', 'lg', 'md', 'sm', ''], lg: ['lg', 'md', 'sm', ''], md: ['md', 'sm', ''], sm: ['sm', ''], xs: [''], xxs: [''] };
    for (const key of chains[bp] ?? ['']) {
      const re = key ? new RegExp(`(?:^|\\s)col-${key}-(\\d{1,2})(?=\\s|$)`) : /(?:^|\s)col-(\d{1,2})(?=\s|$)/;
      const m = cls.match(re);
      if (m?.[1]) return this.clamp(Number(m[1]), 1, 12);
    }
    return 12;
  }
  private normalizeType(type: unknown): string {
    return typeof type === 'string' ? type.trim().toLowerCase() : '';
  }
  private firstEnabledFlag(...values: unknown[]): boolean | null {
    for (const value of values) {
      const enabled = this.toEnabledFlag(value);
      if (enabled !== null) return enabled;
    }
    return null;
  }
  private toEnabledFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') return value;
    if (!value || typeof value !== 'object') return null;

    const cfg = value as Record<string, unknown>;
    if (typeof cfg['enabled'] === 'boolean') return cfg['enabled'];
    if (typeof cfg['enable'] === 'boolean') return cfg['enable'];
    if (typeof cfg['visible'] === 'boolean') return cfg['visible'];
    if (typeof cfg['show'] === 'boolean') return cfg['show'];

    return null;
  }
  private mapStrings(v: unknown): string[] {
    if (!Array.isArray(v)) return [];
    const out = new Set<string>();
    for (const item of v) {
      const normalized = this.normalizeType(item);
      if (normalized) out.add(normalized);
    }
    return [...out];
  }
  private mapNumbers(v: any, fallback: Record<string, number>): Record<string, number> {
    if (!v || typeof v !== 'object') return { ...fallback };
    const out: Record<string, number> = {};
    for (const [k, val] of Object.entries(v)) if (typeof val === 'number' && Number.isFinite(val) && val >= 0) out[k] = val;
    return Object.keys(out).length ? out : { ...fallback };
  }
  private num(v: unknown, fb: number): number {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : fb;
  }
  private clamp(v: number, min: number, max: number): number { return Math.min(max, Math.max(min, Math.round(v))); }

  private isGridPersistenceEnabled(config: any): boolean {
    const gridConfig = config?.layoutConfig ?? config?.gridLayout ?? {};
    return gridConfig?.persist !== false;
  }

  private buildPersistedLayoutSnapshot(): PersistedLayoutSnapshot {
    return {
      version: this.grid.version,
      layouts: this.cloneLayouts(this.layouts) ?? {},
    };
  }

  private cloneLayouts(layouts: Record<string, GridItem[]> | null | undefined): Record<string, GridItem[]> | null {
    if (!layouts || typeof layouts !== 'object') return null;
    return JSON.parse(JSON.stringify(layouts));
  }

  private cloneJson<T>(value: T): T {
    return value == null ? value : JSON.parse(JSON.stringify(value));
  }

  private readPersistedFromLocalStorage(
    persistEnabled = this.grid.persist,
    ignoreActiveLayout = false
  ): Record<string, GridItem[]> | null {
    if (!persistEnabled) return null;
    if (!ignoreActiveLayout && this.layoutService.getActiveLayoutId()) return null;
    try {
      const raw = localStorage.getItem(this.persistKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return this.normalizePersistedLayouts(parsed);
    } catch {
      return null;
    }
  }

  private writePersistedToLocalStorage(snapshot: PersistedLayoutSnapshot): void {
    try {
      localStorage.setItem(this.persistKey(), JSON.stringify(snapshot));
    } catch { }
  }

  private loadPersistedLayouts(config: any): Observable<Record<string, GridItem[]> | null> {
    const fallback = this.readPersistedFromLocalStorage(
      this.isGridPersistenceEnabled(config),
      true,
    );
    const request = this.resolveLayoutPersistenceRequest(config, 'load');
    if (!request) {
      this.persistedLayoutRecord = null;
      return of(fallback);
    }

    const persistence = this.resolveLayoutPersistenceConfig(config);
    if (persistence && this.isEmailOwnedLayoutPersistence(persistence)) {
      return this.loadPersistedLayoutsByEmail(config, request, fallback);
    }

    return this.requestPersistedLayout(request, null, 'RESOLVE').pipe(
      map((response) => {
        this.persistedLayoutRecord = null;
        return this.extractPersistedLayouts(
          response,
          request.responsePath,
          this.resolveLayoutDashboardId(request),
        ) ?? fallback;
      }),
      catchError((err) => {
        console.error('Error loading persisted dashboard layout:', err);
        return of(fallback);
      }),
    );
  }

  private writePersistedToApi(
    snapshot: PersistedLayoutSnapshot,
    scope: LayoutPersistenceScope
  ): void {
    const request = this.resolveLayoutPersistenceRequest(this.configurations, 'save');
    if (!request) return;

    const persistence = this.resolveLayoutPersistenceConfig(this.configurations);
    if (persistence && this.isEmailOwnedLayoutPersistence(persistence)) {
      this.writePersistedToApiByEmail(snapshot, request, persistence);
      return;
    }

    this.subs.add(
      this.requestPersistedLayout(request, snapshot, scope)
        .pipe(
          catchError((err) => {
            console.error('Error saving dashboard layout:', err);
            return of(null);
          }),
        )
        .subscribe()
    );
  }

  private loadPersistedLayoutsByEmail(
    config: any,
    request: LayoutPersistenceRequestConfig,
    fallback: Record<string, GridItem[]> | null,
  ): Observable<Record<string, GridItem[]> | null> {
    const user = this.getCurrentLayoutUserContext();
    const currentEmail = user.email;
    if (!currentEmail) {
      this.persistedLayoutRecord = null;
      return of(fallback);
    }

    const lookupEmails = [
      currentEmail,
      ...this.resolveFallbackAdminEmails(config, currentEmail),
    ];

    return this.resolvePersistedLayoutsByEmail(request, lookupEmails, currentEmail, 0).pipe(
      map((result: PersistedLayoutLookupResult) => {
        this.persistedLayoutRecord = result.record;
        return result.layouts ?? fallback;
      }),
      catchError((err) => {
        console.error('Error loading persisted dashboard layout:', err);
        this.persistedLayoutRecord = null;
        return of(fallback);
      }),
    );
  }

  private resolvePersistedLayoutsByEmail(
    request: LayoutPersistenceRequestConfig,
    lookupEmails: string[],
    currentEmail: string,
    index: number,
  ): Observable<PersistedLayoutLookupResult> {
    if (index >= lookupEmails.length) {
      return of({
        layouts: null,
        record: null,
      });
    }

    const email = lookupEmails[index];
    const emailRequest = this.buildEmailLoadRequest(request, email);

    return this.requestPersistedLayout(emailRequest, null, 'RESOLVE').pipe(
      map((response) => {
        const dashboardId = this.resolveLayoutDashboardId(emailRequest);
        const record = this.extractPersistedLayoutRecord(response, dashboardId);
        if (!record) return null;

        const layouts = this.extractPersistedLayouts(
          response,
          emailRequest.responsePath,
          dashboardId,
        );
        if (!layouts) return null;

        return {
          layouts,
          record: this.normalizePersistedLayoutRecord(record, email, currentEmail),
        };
      }),
      catchError((err) => {
        console.error(`Error loading persisted dashboard layout for ${email}:`, err);
        return of(null);
      }),
      switchMap((result: PersistedLayoutLookupResult | null) => result
        ? of(result)
        : this.resolvePersistedLayoutsByEmail(request, lookupEmails, currentEmail, index + 1))
    );
  }

  private writePersistedToApiByEmail(
    snapshot: PersistedLayoutSnapshot,
    request: LayoutPersistenceRequestConfig,
    persistence: LayoutPersistenceConfig,
  ): void {
    const user = this.getCurrentLayoutUserContext();
    const email = user.email;
    if (!email) {
      console.error('Cannot save dashboard layout: logged-in user email is unavailable.');
      return;
    }

    const role = this.resolveEmailOwnedLayoutRole(persistence, email);
    const isCurrentUserRecord = this.isPersistedLayoutOwnedByEmail(email);
    const uuid = isCurrentUserRecord
      ? this.persistedLayoutRecord?.uuid ?? this.resolvePersistedLayoutUuid(this.persistedLayoutRecord?.raw) ?? null
      : null;
    const operation: 'insert' | 'update' = isCurrentUserRecord ? 'update' : 'insert';
    if (operation === 'update' && !uuid) {
      console.error('Cannot update dashboard layout: existing user layout was found but uuid is missing.');
      return;
    }

    const emailRequest = this.buildEmailSaveRequest(request, email, role, operation, uuid);

    this.subs.add(
      this.requestPersistedLayout(emailRequest, snapshot, 'USER')
        .pipe(
          tap((response) => this.capturePersistedLayoutRecordAfterSave(response, email, role, operation, emailRequest)),
          catchError((err) => {
            console.error('Error saving dashboard layout:', err);
            return of(null);
          }),
        )
        .subscribe()
    );
  }

  private resolveLayoutPersistenceRequest(
    config: any,
    mode: 'load' | 'save'
  ): LayoutPersistenceRequestConfig | null {
    const raw = this.resolveLayoutPersistenceConfig(config);
    if (!raw) return null;

    const { load, save, ...base } = raw;
    const override = mode === 'load' ? load : save;
    const endpoint = {
      ...base,
      ...(override && typeof override === 'object' ? override : {}),
    } as LayoutPersistenceRequestConfig;
    const apiUrl = String(endpoint.apiUrl ?? '').trim();

    if (endpoint.enabled === false || !apiUrl) return null;

    return {
      ...endpoint,
      apiUrl,
    };
  }

  private resolveLayoutPersistenceConfig(config: any): LayoutPersistenceConfig | null {
    const gridConfig = config?.layoutConfig ?? config?.gridLayout ?? {};
    const raw =
      config?.layoutPersistence ??
      gridConfig?.layoutPersistence;

    return raw && typeof raw === 'object'
      ? raw as LayoutPersistenceConfig
      : null;
  }

  private isEmailOwnedLayoutPersistence(config: LayoutPersistenceConfig | null): boolean {
    return config?.emailOwnerMode === true;
  }

  private buildEmailLoadRequest(
    request: LayoutPersistenceRequestConfig,
    email: string,
  ): LayoutPersistenceRequestConfig {
    const customPayload = this.cloneJson(request.customPayload ?? {}) as Record<string, unknown>;
    delete customPayload['requestData'];
    customPayload['email_id'] = [email];
    delete customPayload['uuid'];

    const dashboardId = this.resolveLayoutDashboardId(request);
    if (dashboardId) {
      customPayload['dashboard_id'] = [dashboardId];
    }

    return {
      ...request,
      includeRequestContext: false,
      skipDefaultPayload: true,
      requestData: {},
      defaultRequestData: {},
      customPayload,
    };
  }

  private buildEmailSaveRequest(
    request: LayoutPersistenceRequestConfig,
    email: string,
    role: 'Admin' | 'User',
    operation: 'insert' | 'update',
    uuid: string | null,
  ): LayoutPersistenceRequestConfig {
    const customPayload = this.cloneJson(request.customPayload ?? {}) as Record<string, unknown>;
    delete customPayload['requestData'];

    const requestData = this.cloneJson(request.requestData ?? request.defaultRequestData ?? {}) as Record<string, unknown>;
    requestData['email_id'] = email;

    const dashboardId = this.resolveLayoutDashboardId(request);
    if (dashboardId) {
      requestData['dashboard_id'] = dashboardId;
    }

    requestData['role'] = role;
    requestData['operation'] = operation;

    if (operation === 'update' && uuid) {
      requestData['uuid'] = uuid;
    } else {
      delete requestData['uuid'];
    }

    return {
      ...request,
      includeRequestContext: false,
      skipDefaultPayload: true,
      requestData,
      defaultRequestData: requestData,
      customPayload,
    };
  }

  private resolveLayoutDashboardId(request?: LayoutPersistenceRequestConfig | null): string | null {
    const direct = this.firstString(
      request?.dashboard_id,
      request?.dashboardId,
      typeof request?.requestData?.['dashboard_id'] === 'string' ? request.requestData['dashboard_id'] : null,
      typeof request?.requestData?.['dashboardId'] === 'string' ? request.requestData['dashboardId'] : null,
      typeof request?.defaultRequestData?.['dashboard_id'] === 'string' ? request.defaultRequestData['dashboard_id'] : null,
      typeof request?.defaultRequestData?.['dashboardId'] === 'string' ? request.defaultRequestData['dashboardId'] : null,
      this.configFileName,
      this.sourceKey,
    );

    return direct ? direct.trim() : null;
  }

  private extractPersistedLayoutRecords(response: any): any[] {
    if (Array.isArray(response)) {
      return response.filter(Boolean);
    }

    if (response?.responseList?.[0]?.data?.length) {
      return response.responseList[0].data.filter(Boolean);
    }

    if (Array.isArray(response?.data)) {
      return response.data.filter(Boolean);
    }

    return response ? [response] : [];
  }

  private extractPersistedLayoutRecord(
    response: any,
    dashboardId?: string | null,
  ): any | null {
    const records = this.extractPersistedLayoutRecords(response);
    if (!records.length) return response ?? null;

    const expectedDashboardId = this.normalizeDashboardId(dashboardId);
    if (!expectedDashboardId) {
      return records[0] ?? null;
    }

    return records.find((record) =>
      this.sameDashboardId(
        this.resolvePersistedLayoutDashboardId(record),
        expectedDashboardId,
      )
    ) ?? null;
  }

  private resolvePersistedLayoutDashboardId(record: any): string | null {
    const direct = this.firstString(
      record?.dashboard_id,
      record?.dashboardId,
      record?.requestData?.dashboard_id,
      record?.requestData?.dashboardId,
      record?.data?.dashboard_id,
      record?.data?.dashboardId,
    );

    return direct ? direct.trim() : null;
  }

  private normalizeDashboardId(value: string | null | undefined): string | null {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    return trimmed || null;
  }

  private sameDashboardId(a: string | null | undefined, b: string | null | undefined): boolean {
    const left = this.normalizeDashboardId(a)?.toLowerCase();
    const right = this.normalizeDashboardId(b)?.toLowerCase();
    return !!left && !!right && left === right;
  }

  private normalizePersistedLayoutRecord(
    record: any,
    sourceEmail: string,
    currentEmail: string,
  ): PersistedLayoutRecord {
    return {
      emailId: this.firstString(record?.email_id, record?.emailId, record?.email) ?? sourceEmail,
      uuid: this.resolvePersistedLayoutUuid(record),
      role: this.firstString(record?.role, record?.scope_type),
      sourceEmail,
      isFallback: !this.sameEmail(sourceEmail, currentEmail),
      raw: record,
    };
  }

  private capturePersistedLayoutRecordAfterSave(
    response: any,
    email: string,
    role: 'Admin' | 'User',
    operation: 'insert' | 'update',
    request?: LayoutPersistenceRequestConfig | null,
  ): void {
    const savedRecord = this.extractPersistedLayoutRecord(response, this.resolveLayoutDashboardId(request));
    const uuid = this.resolvePersistedLayoutUuid(savedRecord)
      ?? (operation === 'update' ? (this.persistedLayoutRecord?.uuid ?? null) : null);

    this.persistedLayoutRecord = {
      emailId: this.firstString(savedRecord?.email_id, savedRecord?.emailId, savedRecord?.email) ?? email,
      uuid,
      role: this.firstString(savedRecord?.role, savedRecord?.scope_type) ?? role,
      sourceEmail: email,
      isFallback: false,
      raw: savedRecord,
    };
  }

  private resolvePersistedLayoutUuid(record: any): string | null {
    const direct = this.firstString(
      record?.uuid,
      record?.UUID,
      record?.layout_uuid,
      record?.layoutUuid,
      record?.requestData?.uuid,
      record?.data?.uuid,
    );
    if (direct) return direct;

    const stringId = typeof record?.id === 'string' ? record.id.trim() : '';
    return this.isUuidLike(stringId) ? stringId : null;
  }

  private resolveFallbackAdminEmails(
    config: any,
    currentEmail?: string | null,
  ): string[] {
    const persistence = this.resolveLayoutPersistenceConfig(config);
    const configured = [
      ...(Array.isArray(persistence?.fallbackAdminEmails) ? persistence!.fallbackAdminEmails : []),
      ...(typeof persistence?.fallbackAdminEmail === 'string' ? [persistence.fallbackAdminEmail] : []),
    ];
    const source = configured.length
      ? configured
      : (Array.isArray(persistence?.adminEmails) ? persistence!.adminEmails : []);
    const current = this.normalizeEmail(currentEmail);
    const seen = new Set<string>();
    const emails: string[] = [];

    for (const value of source) {
      const trimmed = String(value || '').trim();
      const normalized = this.normalizeEmail(trimmed);
      if (!trimmed || !normalized || normalized === current || seen.has(normalized)) continue;
      seen.add(normalized);
      emails.push(trimmed);
    }

    return emails;
  }

  private resolveEmailOwnedLayoutRole(
    config: LayoutPersistenceConfig | null,
    email: string,
  ): 'Admin' | 'User' {
    const normalized = this.normalizeEmail(email);
    const adminEmails = new Set(
      (config?.adminEmails || [])
        .map((value) => this.normalizeEmail(value))
        .filter((value): value is string => !!value)
    );

    return normalized && adminEmails.has(normalized) ? 'Admin' : 'User';
  }

  private isPersistedLayoutOwnedByEmail(email: string): boolean {
    if (!this.persistedLayoutRecord || this.persistedLayoutRecord.isFallback) return false;

    return this.sameEmail(
      this.persistedLayoutRecord.emailId ?? this.persistedLayoutRecord.sourceEmail,
      email,
    );
  }

  private normalizeEmail(value: unknown): string | null {
    return typeof value === 'string' && value.trim()
      ? value.trim().toLowerCase()
      : null;
  }

  private isUuidLike(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  private sameEmail(left: unknown, right: unknown): boolean {
    const a = this.normalizeEmail(left);
    const b = this.normalizeEmail(right);
    return !!a && !!b && a === b;
  }

  private requestPersistedLayout(
    request: LayoutPersistenceRequestConfig,
    snapshot?: PersistedLayoutSnapshot | null,
    mode: LayoutPersistenceRequestMode = 'RESOLVE'
  ) {
    const method = String(request.method ?? 'post').trim().toLowerCase() || 'post';
    const layoutKey = String(request.layoutKey ?? 'layout').trim() || 'layout';
    const requestContext = request.includeRequestContext === false
      ? {}
      : this.buildLayoutPersistenceRequestContext(mode);
    const defaultRequestData = this.cloneJson(
      {
        ...(request.requestData ?? request.defaultRequestData ?? {}),
        ...requestContext,
      }
    ) as Record<string, unknown>;
    const customPayload = this.cloneJson(request.customPayload ?? {}) as Record<string, unknown>;
    const data: Record<string, unknown> = {
      ...request,
      headers: request.headers,
      useQueryParam: request.useQueryParams === true,
    };

    if (request.skipDefaultPayload) {
      const payload =
        customPayload && typeof customPayload === 'object' ? customPayload : {};
      const payloadRequestData =
        payload['requestData'] && typeof payload['requestData'] === 'object'
          ? payload['requestData'] as Record<string, unknown>
          : {};
      const mergedRequestData = {
        ...defaultRequestData,
        ...payloadRequestData,
      };

      if (Object.keys(mergedRequestData).length) {
        payload['requestData'] = mergedRequestData;
      }

      data['customPayload'] = payload;
      data['skipDefaultPayload'] = true;
    } else {
      data['defaultRequestData'] = defaultRequestData;
      if (Object.keys(customPayload).length) {
        data['customPayload'] = customPayload;
      }
    }

    return this.configService.getConfig(
      request.apiUrl!,
      data,
      {
        requestParams: snapshot ? { [layoutKey]: snapshot } : {},
        queryParams: this.cloneJson(request.queryParams ?? {}),
        useQueryParams: request.useQueryParams === true,
        headers: request.headers,
      },
      method,
    );
  }

  private buildLayoutPersistenceRequestContext(
    mode: LayoutPersistenceRequestMode
  ): Record<string, unknown> {
    const user = this.getCurrentLayoutUserContext();
    const context: Record<string, unknown> = {
      scopeType: mode,
      resolveScopes: ['USER', 'GLOBAL'],
      roleIds: user.roleIds,
    };

    if (user.userId) {
      context['userId'] = user.userId;
    }

    if (user.username) {
      context['username'] = user.username;
    }

    if (mode === 'USER') {
      context['scopeId'] = user.userId || 'anonymous';
    } else if (mode === 'GLOBAL') {
      context['scopeId'] = 'GLOBAL';
    }

    if (mode !== 'RESOLVE') {
      delete context['resolveScopes'];
    }

    return context;
  }

  private getCurrentLayoutUserContext(): LayoutUserContext {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const tokenParsed: any = keycloak?.tokenParsed ?? {};
    const realmRoles = Array.isArray(tokenParsed?.realm_access?.roles)
      ? tokenParsed.realm_access.roles
      : [];
    const resourceRoles = Object.values(tokenParsed?.resource_access ?? {})
      .flatMap((resource: any) => Array.isArray(resource?.roles) ? resource.roles : []);
    const roleIds = [...new Set(
      [...realmRoles, ...resourceRoles]
        .map((role) => String(role || '').trim())
        .filter((role) => !!role)
    )];

    return {
      userId: this.firstString(tokenParsed?.sub, keycloak?.subject),
      username: this.firstString(
        tokenParsed?.preferred_username,
        tokenParsed?.name
      ),
      email: this.firstString(
        tokenParsed?.email,
        typeof tokenParsed?.preferred_username === 'string' && tokenParsed.preferred_username.includes('@')
          ? tokenParsed.preferred_username
          : null,
      ),
      roleIds,
    };
  }

  private saveLayout(scope: LayoutPersistenceScope): void {
    if (!this.editMode) return;

    if (this.interaction) {
      this.finishInteraction(false);
    }

    this.writePersisted(scope);
    this.syncDashboardAfterLayoutChange();
    this.exitEditMode();
  }

  private exitEditMode(): void {
    this.editMode = false;
    this.activeDragId = null;
    this.activeResizeId = null;
    this.editSessionLayouts = null;
  }

  private syncDashboardAfterLayoutChange(): void {
    this.updateRowsForResponsiveService();
    this.queueCompactFitMeasurement();
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  private extractPersistedLayouts(
    response: any,
    responsePath?: string,
    dashboardId?: string | null,
  ): Record<string, GridItem[]> | null {
    const matchedRecord = dashboardId
      ? this.extractPersistedLayoutRecord(response, dashboardId)
      : null;
    if (dashboardId && !matchedRecord) return null;

    const source = matchedRecord ?? response;
    const preferred = responsePath
      ? this.readPathValue(source, responsePath)
      : source;
    const candidates = [
      preferred,
      preferred?.layouts,
      preferred?.layout,
      preferred?.layout_json,
      preferred?.gridLayout,
      preferred?.savedLayout,
      preferred?.savedLayouts,
      source?.layouts,
      source?.layout,
      source?.layout_json,
      source?.gridLayout,
      source?.savedLayout,
      source?.savedLayouts,
      source?.requestData?.layout,
      source?.requestData?.layouts,
      source?.requestData?.layout_json,
      source?.data?.layout,
      source?.data?.layouts,
      source?.data?.layout_json,
      response?.layouts,
      response?.layout,
      response?.layout_json,
      response?.gridLayout,
      response?.savedLayout,
      response?.savedLayouts,
      response?.requestData?.layout,
      response?.requestData?.layouts,
      response?.requestData?.layout_json,
      response?.data?.layout,
      response?.data?.layouts,
      response?.data?.layout_json,
    ];

    for (const candidate of candidates) {
      const normalized = this.normalizePersistedLayouts(candidate);
      if (normalized) return normalized;
    }

    return null;
  }

  private normalizePersistedLayouts(candidate: any): Record<string, GridItem[]> | null {
    if (!candidate) return null;

    let parsed = candidate;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return null;
      }
    }

    const rawLayouts =
      parsed?.layouts && typeof parsed.layouts === 'object'
        ? parsed.layouts
        : parsed;

    if (!rawLayouts || typeof rawLayouts !== 'object' || Array.isArray(rawLayouts)) {
      return null;
    }

    const entries = Object.entries(rawLayouts)
      .filter(([, value]) => Array.isArray(value));

    if (!entries.length) return null;

    return entries.reduce((acc, [bp, value]) => {
      acc[bp] = (value as Array<Record<string, unknown>>)
        .map((item) => ({ ...item } as unknown as GridItem));
      return acc;
    }, {} as Record<string, GridItem[]>);
  }

  private readPathValue(source: any, path?: string): any {
    if (!path?.trim()) return source;

    return path
      .split('.')
      .map((segment) => segment.trim())
      .filter((segment) => !!segment)
      .reduce((current: any, segment) => {
        if (current == null) return undefined;
        if (Array.isArray(current) && /^\d+$/.test(segment)) {
          return current[Number(segment)];
        }
        return current?.[segment];
      }, source);
  }
}
